import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  AutoAwesome as AIIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  AccessTime as TimeIcon,
  Today as TodayIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as CheckIcon,
  Celebration as CelebrationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import ApiService from '../../services/api';
import GMBService from '../../services/GMBService';
import logger from '../../utils/logger';
import EnhancedPostPreview from '../PostGenerationResults/components/EnhancedPostPreview';
import { enhancedTemplateData } from '../PostGenerationResults/EnhancedTemplateData';
import { useDefaultProfile } from '../../contexts/DefaultProfileContext';
import { useProfilePlan } from '../../contexts/ProfilePlanContext';
import { API_SERVER_URL } from '../../config/api';
import FullCalendarView from './FullCalendarView';
import './MonthlyPlan.css';

// Import festival images from the EDM folder
import diwali1 from '../edms/visibeen/diwali1.jpeg';
import diwali2 from '../edms/visibeen/diwali2.jpeg';
import diwali3 from '../edms/visibeen/diwali3.jpeg';
import diwali4 from '../edms/visibeen/diwali4.jpeg';
import dhanteras1 from '../edms/visibeen/dhanteras1.jpg';
import dhanteras2 from '../edms/visibeen/dhanteras2.jpg';
import dhanteras3 from '../edms/visibeen/dhanteras3.jpg';
import dhanteras4 from '../edms/visibeen/dhanteras4.jpg';
import vishwakarma1 from '../edms/visibeen/vishwakarma1.jpg';
import vishwakarma2 from '../edms/visibeen/vishwakarma2.jpg';
import vishwakarma3 from '../edms/visibeen/vishwakarma3.jpg';
import vishwakarma4 from '../edms/visibeen/vishwakarma4.jpg';
import bhaidooj1 from '../edms/visibeen/bhaidooj1.jpg';
import bhaidooj2 from '../edms/visibeen/bhaidooj2.jpg';
import bhaidooj3 from '../edms/visibeen/bhaidooj3.jpg';
import bhaidooj4 from '../edms/visibeen/bhaidooj4.jpg';
import navratri1 from '../edms/visibeen/navratri1.jpg';
import navratri2 from '../edms/visibeen/navratri2.jpg';
import navratri3 from '../edms/visibeen/navratri3.jpg';
import dussehra1 from '../edms/visibeen/dussehra1.jpg';
import dussehra2 from '../edms/visibeen/dussehra2.jpg';
import dussehra3 from '../edms/visibeen/dussehra3.jpg';
import holi3 from '../edms/visibeen/holi3.jpg';
import holi4 from '../edms/visibeen/holi4.jpg';

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const API_CALL_DEBOUNCE = 10000; // 10 seconds to prevent excessive API calls

// ============================================================================
// MULTI-TAB COORDINATION SYSTEM (USER-SCOPED)
// ============================================================================

/**
 * Get current user ID for scoping localStorage keys
 * This prevents conflicts when multiple users are logged in on different browsers/tabs
 */
const getUserId = () => {
  try {
    // Check for admin viewing client
    const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
    if (adminAccessDataRaw) {
      const adminAccess = JSON.parse(adminAccessDataRaw);
      if (adminAccess?.targetUserId) {
        return `admin_view_${adminAccess.targetUserId}`;
      }
    }

    // Get regular user ID
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id || user.user_id || 'anonymous';
    }
  } catch (e) {
    console.warn('[MonthlyPlan] Failed to get user ID:', e);
  }
  return 'anonymous';
};

// USER-SCOPED localStorage keys - prevents cross-user conflicts
const currentUserId = getUserId();
const CACHE_KEY_PREFIX = `monthly_plan_${currentUserId}_`;
const API_LOCK_KEY = `monthly_plan_api_lock_${currentUserId}`;
const TAB_HEARTBEAT_KEY = `monthly_plan_tab_heartbeat_${currentUserId}`;

// Generate unique tab ID with user context
const TAB_ID = `tab_${currentUserId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const API_LOCK_DURATION = 5000; // 5 seconds
const HEARTBEAT_INTERVAL = 3000; // 3 seconds

// Tab-aware API lock system
const acquireAPILock = () => {
  try {
    const lock = localStorage.getItem(API_LOCK_KEY);
    if (lock) {
      const lockData = JSON.parse(lock);
      const now = Date.now();

      // If lock is held by another tab and not expired, deny
      if (lockData.tabId !== TAB_ID && (now - lockData.timestamp) < API_LOCK_DURATION) {
        return false;
      }
    }

    // Acquire lock for this tab
    localStorage.setItem(API_LOCK_KEY, JSON.stringify({
      tabId: TAB_ID,
      timestamp: Date.now()
    }));
    return true;
  } catch (e) {
    return true; // If localStorage fails, allow the call
  }
};

const releaseAPILock = () => {
  try {
    const lock = localStorage.getItem(API_LOCK_KEY);
    if (lock) {
      const lockData = JSON.parse(lock);
      // Only release if we own the lock
      if (lockData.tabId === TAB_ID) {
        localStorage.removeItem(API_LOCK_KEY);
      }
    }
  } catch (e) {
    // Silent fail
  }
};

// Tab heartbeat system to detect closed tabs
const updateTabHeartbeat = () => {
  try {
    const heartbeats = JSON.parse(localStorage.getItem(TAB_HEARTBEAT_KEY) || '{}');
    heartbeats[TAB_ID] = Date.now();

    // Clean up old heartbeats (tabs closed > 10 seconds ago)
    const now = Date.now();
    Object.keys(heartbeats).forEach(tabId => {
      if (now - heartbeats[tabId] > 10000) {
        delete heartbeats[tabId];
      }
    });

    localStorage.setItem(TAB_HEARTBEAT_KEY, JSON.stringify(heartbeats));
  } catch (e) {
    // Silent fail
  }
};

const cleanupTabResources = () => {
  try {
    // Release any locks held by this tab
    releaseAPILock();

    // Remove our heartbeat
    const heartbeats = JSON.parse(localStorage.getItem(TAB_HEARTBEAT_KEY) || '{}');
    delete heartbeats[TAB_ID];
    localStorage.setItem(TAB_HEARTBEAT_KEY, JSON.stringify(heartbeats));
  } catch (e) {
    // Silent fail
  }
};

// Check if we're the primary tab (first to acquire lock or oldest tab)
const isPrimaryTab = () => {
  try {
    const heartbeats = JSON.parse(localStorage.getItem(TAB_HEARTBEAT_KEY) || '{}');
    const tabIds = Object.keys(heartbeats).sort((a, b) => heartbeats[a] - heartbeats[b]);
    return tabIds.length === 0 || tabIds[0] === TAB_ID;
  } catch (e) {
    return true; // Default to primary if check fails
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Plan-based post limits
const getMaxPostsByPlan = (planCode) => {
  switch (planCode?.toLowerCase()) {
    case 'pro': return 20;
    case 'advance': case 'advanced': return 10;
    case 'basic': default: return 5;
  }
};

/**
 * Format description with keywords and hashtags for GMB posts
 * @param {string} baseDescription - The base description text
 * @param {string[]} keywords - Array of keywords
 * @param {string[]} hashtags - Array of hashtags
 * @returns {string} Formatted description with keywords and hashtags
 */
const formatDescriptionWithTags = (baseDescription, keywords = [], hashtags = []) => {
  let description = baseDescription || '';

  // Add keywords (as natural text)
  if (keywords && keywords.length > 0) {
    const keywordText = keywords.join(', ');
    description += `\n\n${keywordText}`;
  }

  // Add hashtags
  if (hashtags && hashtags.length > 0) {
    const hashtagText = hashtags.join(' ');
    description += `\n${hashtagText}`;
  }

  return description.trim();
};

/**
 * Calculate evenly distributed day offsets for posts over a period
 * @param {number} totalPosts - Number of posts to distribute
 * @param {number} daysInPeriod - Total days to distribute over (e.g., 30)
 * @param {Set} existingDays - Set of days already taken
 * @returns {number[]} Array of day offsets for each post
 */
const calculateDistributedDays = (totalPosts, daysInPeriod, existingDays = new Set()) => {
  if (totalPosts <= 0 || daysInPeriod <= 0) return [];

  // Calculate ideal gap between posts
  const gap = daysInPeriod / totalPosts;
  const distributedDays = [];

  for (let i = 0; i < totalPosts; i++) {
    // Calculate target day (center of each slot)
    let targetDay = Math.floor(i * gap + gap / 2);

    // Ensure we stay within bounds
    targetDay = Math.min(targetDay, daysInPeriod - 1);
    targetDay = Math.max(targetDay, 0);

    // Find nearest available day if target is taken
    let offset = 0;
    let foundDay = null;

    while (foundDay === null && offset < daysInPeriod) {
      // Try target day first, then alternate above/below
      const tryDay = targetDay + (offset % 2 === 0 ? offset / 2 : -Math.ceil(offset / 2));

      if (tryDay >= 0 && tryDay < daysInPeriod && !existingDays.has(tryDay)) {
        foundDay = tryDay;
      }
      offset++;
    }

    if (foundDay !== null) {
      distributedDays.push(foundDay);
      existingDays.add(foundDay);
    }
  }

  return distributedDays.sort((a, b) => a - b);
};

/**
 * Generate AI-powered description for a post using OpenAI
 * @param {string} postType - Type of post: 'festival', 'review', 'gmb-photo'
 * @param {Object} context - Context data for the post
 * @returns {Promise<Object>} - Generated description, keywords, hashtags
 */
const generateAIPostDescription = async (postType, context) => {
  try {
    const response = await ApiService.post('/customer/ai/generate-post-description', {
      postType,
      context
    });

    const structured = response?.data?.structured || response?.structured;
    if (structured && structured.description) {
      logger.log(`[MonthlyPlan] AI generated ${postType} description successfully`);
      return {
        description: structured.description || '',
        caption: structured.caption || '',
        keywords: structured.keywords || [],
        hashtags: structured.hashtags || []
      };
    }

    logger.warn(`[MonthlyPlan] AI description generation returned empty for ${postType}`);
    return null;
  } catch (err) {
    logger.warn(`[MonthlyPlan] Failed to generate AI description for ${postType}:`, err?.message);
    return null;
  }
};

// Festival EDM data
const FESTIVAL_EDM_DATA = [
  { name: 'Diwali', emoji: '🪔', template: 'diwali', images: [diwali1, diwali2, diwali3, diwali4] },
  { name: 'Dhanteras', emoji: '✨', template: 'dhanteras', images: [dhanteras1, dhanteras2, dhanteras3, dhanteras4] },
  { name: 'Vishwakarma Day', emoji: '🔧', template: 'vishwakarma', images: [vishwakarma1, vishwakarma2, vishwakarma3, vishwakarma4] },
  { name: 'Bhai Dooj', emoji: '👫', template: 'bhaidooj', images: [bhaidooj1, bhaidooj2, bhaidooj3, bhaidooj4] },
  { name: 'Navratri', emoji: '🙏', template: 'navratri', images: [navratri1, navratri2, navratri3] },
  { name: 'Dussehra', emoji: '🏹', template: 'dussehra', images: [dussehra1, dussehra2, dussehra3] },
  { name: 'Holi', emoji: '🎨', template: 'holi', images: [holi3, holi4] }
];

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads')) {
    return `${API_SERVER_URL}${url}`;
  }
  return url;
};

// ============================================================================
// STYLED COMPONENTS - Mobile-First Design
// ============================================================================

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: '#ffffff',
  padding: '12px',
  paddingBottom: '100px', // Space for mobile bottom action bar
  // Desktop
  [theme.breakpoints.up('md')]: {
    padding: '32px',
    paddingBottom: '32px'
  }
}));

const HeaderCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  // Desktop
  [theme.breakpoints.up('md')]: {
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '32px',
    gap: '20px'
  }
}));

const ProfileCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  background: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  color: '#1e293b',
  flexWrap: 'wrap',
  // Desktop
  [theme.breakpoints.up('md')]: {
    gap: '20px',
    padding: '20px 24px',
    borderRadius: '12px',
    marginBottom: '20px',
    flexWrap: 'nowrap'
  }
}));

const ProfileAvatar = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: '#667eea',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 700,
  color: 'white',
  flexShrink: 0,
  // Desktop
  [theme.breakpoints.up('md')]: {
    width: '56px',
    height: '56px',
    fontSize: '18px'
  }
}));

const ActionButton = styled(Button)(({ variant, theme }) => ({
  borderRadius: '10px',
  padding: '10px 16px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '13px',
  boxShadow: 'none',
  minHeight: '44px', // Touch-friendly
  background: variant === 'contained' ? '#1e293b' : 'transparent',
  border: variant === 'outlined' ? '1px solid #cbd5e1' : 'none',
  color: variant === 'contained' ? 'white' : '#475569',
  WebkitTapHighlightColor: 'transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: variant === 'contained' ? '#334155' : '#f1f5f9',
    boxShadow: 'none',
    borderColor: '#94a3b8'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  '&.Mui-disabled': {
    background: variant === 'contained' ? '#e2e8f0' : 'transparent',
    color: '#94a3b8'
  },
  // Desktop
  [theme.breakpoints.up('md')]: {
    padding: '8px 20px',
    fontSize: '14px',
    minHeight: 'auto'
  }
}));

const ControlSelect = styled(Select)(({ theme }) => ({
  borderRadius: '10px',
  background: '#ffffff',
  fontSize: '14px',
  minHeight: '44px', // Touch-friendly
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#667eea'
  },
  // Desktop
  [theme.breakpoints.up('md')]: {
    minHeight: 'auto'
  }
}));

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MonthlyPlan = () => {
  const { defaultProfile, setDefaultProfile } = useDefaultProfile();
  const { planCode, selectProfile, isLoading: isPlanLoading, subscription } = useProfilePlan();

  // State Management
  const [isProfileSwitching, setIsProfileSwitching] = useState(false);
  const [stablePlanCode, setStablePlanCode] = useState(planCode || 'basic');
  const [isActiveTab, setIsActiveTab] = useState(true); // MULTI-TAB: Track if this is primary tab

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [plan, setPlan] = useState(null);
  const [postsPerWeek] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preparingPlan, setPreparingPlan] = useState(false);
  const [error, setError] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [fullImageUrl, setFullImageUrl] = useState(null);
  const [cacheTime, setCacheTime] = useState(null);

  // Access control states - lock page until payment verification passes
  const [accessChecking, setAccessChecking] = useState(true);
  const [hasPaymentAccess, setHasPaymentAccess] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [accessDeniedReason, setAccessDeniedReason] = useState(null);
  const [isTrialUser, setIsTrialUser] = useState(false); // Trial user flag (₹1 coupon users)

  const [productServiceDialogOpen, setProductServiceDialogOpen] = useState(false);
  const [productInput, setProductInput] = useState('');
  const [serviceInput, setServiceInput] = useState('');
  const [manualProducts, setManualProducts] = useState([]);
  const [manualServices, setManualServices] = useState([]);
  const [skipProductServicePrompt, setSkipProductServicePrompt] = useState(false);
  const [pendingGenerateAfterPrompt, setPendingGenerateAfterPrompt] = useState(false);
  const [manualDataLoaded, setManualDataLoaded] = useState(false);

  const [scheduling, setScheduling] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState(null);
  const [testimonialLoading, setTestimonialLoading] = useState(false);
  const [testimonialPreviewData, setTestimonialPreviewData] = useState(null);
  const [edmLoading, setEdmLoading] = useState(false);
  const [edmPreviewData, setEdmPreviewData] = useState(null);
  const [adminAiPostLimit, setAdminAiPostLimit] = useState(null);
  const [combinedLoading, setCombinedLoading] = useState(false);
  const [initialPostsLoaded, setInitialPostsLoaded] = useState(false); // Track if initial DB posts have been loaded

  // Generation Progress State - Professional loading popup
  const [generationProgress, setGenerationProgress] = useState({
    isGenerating: false,
    currentPhase: '',
    phaseNumber: 0,
    totalPhases: 5,
    postsGenerated: 0,
    targetPosts: 0,
    startTime: null,
    estimatedTimeRemaining: null,
    phases: [
      { id: 1, name: 'Preparing', status: 'pending', icon: '⚙️' },
      { id: 2, name: 'Festival Posts', status: 'pending', icon: '🎉' },
      { id: 3, name: 'AI Posts', status: 'pending', icon: '🤖' },
      { id: 4, name: 'Review Posts', status: 'pending', icon: '⭐' },
      { id: 5, name: 'Photo Posts', status: 'pending', icon: '📸' }
    ]
  });
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);

  const [profileSelectionDialogOpen, setProfileSelectionDialogOpen] = useState(false);
  const [profileDialogDismissed, setProfileDialogDismissed] = useState(false);
  const [editPostDialogOpen, setEditPostDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    scheduled_time: '',
    post_url: '',
    keywords: '',
    hashtags: ''
  });

  // Refs for optimization
  const isGeneratingRef = useRef(false);
  const testimonialPreviewRef = useRef(null);
  const edmPreviewRef = useRef(null);
  const lastAPICallRef = useRef(0);
  const cachedPostsRef = useRef(null);
  const isLoadingPlanDataRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const heartbeatIntervalRef = useRef(null); // MULTI-TAB: Heartbeat interval

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const gmbAccountId = useMemo(() => {
    if (!defaultProfile) return null;
    return defaultProfile.id || defaultProfile.gmbAccountId || null;
  }, [defaultProfile?.id, defaultProfile?.gmbAccountId]);

  const cacheKey = useMemo(() => {
    if (!defaultProfile) return null;
    let userId = 'anonymous';
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        userId = userData.id || userData.user_id || userData.customer_id || 'anonymous';
      }
    } catch (e) {
      // Silent fail
    }
    const locId = defaultProfile.locationId || defaultProfile.id || 'default';
    return `${CACHE_KEY_PREFIX}${userId}_${locId}`;
  }, [defaultProfile?.locationId, defaultProfile?.id]);

  const productServiceStorageKey = useMemo(() => {
    if (!defaultProfile) return null;
    let userId = 'anonymous';
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        userId = userData.id || userData.user_id || userData.customer_id || 'anonymous';
      }
    } catch (e) {
      // Silent fail
    }
    const locId = defaultProfile.locationId
      || defaultProfile.id
      || defaultProfile.location_id
      || (defaultProfile.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);
    return locId ? `weekly_products_services_${userId}_${locId}` : null;
  }, [defaultProfile?.locationId, defaultProfile?.id, defaultProfile?.location_id, defaultProfile?.locationData?.name]);

  const skipStorageKey = useMemo(() => (
    productServiceStorageKey ? `${productServiceStorageKey}_skip` : null
  ), [productServiceStorageKey]);

  const maxPostsForPlan = useMemo(() => getMaxPostsByPlan(stablePlanCode), [stablePlanCode]);

  const monthDates = useMemo(() => {
    const dates = [];
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startDayOfWeek = firstDayOfMonth.getDay();

    for (let i = 0; i < startDayOfWeek; i++) {
      dates.push({ isEmpty: true, key: `empty-start-${i}` });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;
      const dayOffset = Math.floor((date - today) / (1000 * 60 * 60 * 24));

      dates.push({
        date,
        dayNum: day,
        dayOffset,
        dayName: DAYS_OF_WEEK[date.getDay()],
        month: date.toLocaleString('default', { month: 'short' }),
        isToday,
        isPast,
        isEmpty: false,
        key: `day-${day}`
      });
    }

    return dates;
  }, [currentMonth]);

  const postsByDay = useMemo(() => {
    const map = {};
    if (plan?.posts) {
      plan.posts.forEach((post) => {
        const dayOffset = post.day_offset ?? 0;
        if (!map[dayOffset]) map[dayOffset] = [];
        map[dayOffset].push(post);
      });
    }
    return map;
  }, [plan?.posts]);

  const totalScheduledPosts = useMemo(() => plan?.posts?.length || 0, [plan?.posts?.length]);
  const postsWithImages = useMemo(() => plan?.posts?.filter(p => p.generated_image_url)?.length || 0, [plan?.posts]);

  // Calculate if user can generate new posts
  // Logic: If user has scheduled posts for the next 30 days, show cooldown
  // Once all posts are in the past (>30 days old), they can generate again
  const generationStatus = useMemo(() => {
    // If no posts exist, can generate
    if (!plan?.posts || plan.posts.length === 0) {
      return { canGenerate: true, daysRemaining: 0, upcomingPostsCount: 0 };
    }

    // Count posts scheduled within the next 30 days (day_offset 0-29)
    const postsInNext30Days = plan.posts.filter(post => {
      const dayOffset = post.day_offset ?? 0;
      return dayOffset >= 0 && dayOffset < 30;
    });

    const upcomingPostsCount = postsInNext30Days.length;

    // If there are posts scheduled in the next 30 days, user cannot generate
    if (upcomingPostsCount > 0) {
      // Find the latest day_offset to calculate when generation becomes available
      const maxDayOffset = Math.max(...postsInNext30Days.map(p => p.day_offset ?? 0));
      const daysRemaining = Math.max(0, maxDayOffset + 1); // Days until last post passes

      return {
        canGenerate: false,
        daysRemaining,
        upcomingPostsCount
      };
    }

    // No upcoming posts in next 30 days, user can generate
    return { canGenerate: true, daysRemaining: 0, upcomingPostsCount: 0 };
  }, [plan?.posts]);

  // ============================================================================
  // CALLBACK FUNCTIONS
  // ============================================================================

  // Helper function to update generation progress
  const updateGenerationProgress = useCallback((phaseNumber, phaseName, status, postsCount = null) => {
    setGenerationProgress(prev => {
      const updatedPhases = prev.phases.map(phase => {
        if (phase.id === phaseNumber) {
          return { ...phase, status };
        }
        if (phase.id < phaseNumber && phase.status !== 'completed') {
          return { ...phase, status: 'completed' };
        }
        return phase;
      });

      const elapsed = prev.startTime ? (Date.now() - prev.startTime) / 1000 : 0;
      const progressRatio = phaseNumber / prev.totalPhases;
      const estimatedTotal = progressRatio > 0 ? elapsed / progressRatio : 60;
      const remaining = Math.max(0, Math.round(estimatedTotal - elapsed));

      return {
        ...prev,
        currentPhase: phaseName,
        phaseNumber,
        phases: updatedPhases,
        postsGenerated: postsCount !== null ? postsCount : prev.postsGenerated,
        estimatedTimeRemaining: remaining
      };
    });
  }, []);

  const handlePrevMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  const handleGoToToday = useCallback(() => {
    setCurrentMonth(new Date());
  }, []);

  const normalizeListInput = useCallback((value = '') =>
    value
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean), []);

  const listsMatch = useCallback((first = [], second = []) => {
    if (!Array.isArray(first) || !Array.isArray(second)) return false;
    if (first.length !== second.length) return false;
    const left = [...first].map((item) => String(item || '').toLowerCase()).sort();
    const right = [...second].map((item) => String(item || '').toLowerCase()).sort();
    return left.every((item, index) => item === right[index]);
  }, []);

  const getInitials = useCallback((name) => {
    if (!name) return 'B';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }, []);

  // MULTI-TAB: Modified loadScheduledPostsFromDB with API lock
  const loadScheduledPostsFromDB = useCallback(async (locationId) => {
    try {
      const now = Date.now();
      const timeSinceLastCall = now - lastAPICallRef.current;

      // Check debounce
      if (timeSinceLastCall < API_CALL_DEBOUNCE && cachedPostsRef.current) {
        return cachedPostsRef.current;
      }

      // MULTI-TAB FIX: Try to acquire API lock
      if (!acquireAPILock()) {
        console.log(`[MonthlyPlan Tab ${TAB_ID.substr(-4)}] Another tab is calling API, using cache`);
        return cachedPostsRef.current || [];
      }

      try {
        lastAPICallRef.current = now;

        // Build API URL with locationId as primary filter (most reliable)
        // locationId is consistent regardless of who created the post (admin or user)
        let apiUrl = '/customer/post-scheduler/get-scheduled-posts?pageSize=200';
        
        // Add locationId parameter - this is the preferred way to fetch posts
        if (locationId) {
          const cleanLocationId = String(locationId).replace(/^locations\//, '');
          apiUrl += `&locationId=${cleanLocationId}`;
          console.log(`[MonthlyPlan] Fetching posts by locationId: ${cleanLocationId}`);
        }
        
        // Also add userId for admin viewing (backwards compatibility)
        try {
          const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
          if (adminAccessDataRaw) {
            const adminAccess = JSON.parse(adminAccessDataRaw);
            if (adminAccess?.targetUserId) {
              apiUrl += `&userId=${adminAccess.targetUserId}`;
              console.log(`[MonthlyPlan] Admin viewing user ${adminAccess.targetUserId} posts`);
            }
          }
        } catch (e) {
          // Silent fail - use default URL
        }

        const resp = await ApiService.get(apiUrl);
        const data = resp?.data || resp || {};
        const posts = data.posts || [];

        // Robust location ID normalization - handles all known formats:
        // - accounts/123/locations/456 -> 456
        // - locations/456 -> 456
        // - 123:456 -> 456  
        // - 456 -> 456
        // - null/undefined -> null
        const normalizeLocationId = (id) => {
          if (!id) return null;
          const str = String(id).trim();
          
          // Handle accounts/X/locations/Y format
          if (str.includes('/locations/')) {
            const match = str.match(/\/locations\/([^/]+)/);
            return match ? match[1] : str;
          }
          
          // Handle accountId:locationId format
          if (str.includes(':')) {
            const parts = str.split(':');
            return parts.length === 2 ? parts[1] : str;
          }
          
          // Handle locations/X format (without leading slash)
          if (str.startsWith('locations/')) {
            return str.replace('locations/', '');
          }
          
          // Handle accounts/X format (shouldn't happen for locationId but be safe)
          if (str.startsWith('accounts/')) {
            return str.split('/').pop();
          }
          
          return str;
        };

        if (posts.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // DEBUG: Log posts received from API (already filtered by locationId on backend)
          console.log(`[MonthlyPlan] === POST LOADING DEBUG ===`);
          console.log(`[MonthlyPlan] Total posts from API (filtered by locationId on backend): ${posts.length}`);
          
          if (posts.length > 0) {
            // Log first 3 posts for debugging
            posts.slice(0, 3).forEach((p, i) => {
              try {
                const descData = p.description ? JSON.parse(p.description) : {};
                console.log(`[MonthlyPlan] Post ${i + 1}/${posts.length}: "${p.title || p.post_name}" - locationId="${descData.locationId}"`);
              } catch (e) {
                console.log(`[MonthlyPlan] Post ${i + 1}/${posts.length}: "${p.title || p.post_name}" - description is not JSON`);
              }
            });
          }

          // No frontend filtering needed - backend already filters by locationId
          const convertedPosts = posts
            .map(p => {
              let descData = {};
              try {
                descData = p.description ? JSON.parse(p.description) : {};
              } catch (e) {
                descData = { summary: p.description };
              }

              const scheduledTime = new Date(p.scheduled_time);
              const scheduledDate = new Date(scheduledTime.getFullYear(), scheduledTime.getMonth(), scheduledTime.getDate());
              const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const dayOffset = Math.round((scheduledDate - todayDate) / (1000 * 60 * 60 * 24));
              const hours = scheduledTime.getHours().toString().padStart(2, '0');
              const minutes = scheduledTime.getMinutes().toString().padStart(2, '0');
              const timeOfDay = `${hours}:${minutes}`;

              return {
                db_id: p.id,
                day_offset: dayOffset,
                time_of_day: timeOfDay,
                title: p.title || p.post_name,
                description: descData.summary || p.description || '',
                generated_image_url: p.post_url || descData.media?.sourceUrl || '',
                topicType: descData.topicType || 'STANDARD',
                keywords: descData.keywords || [],
                hashtags: descData.hashtags || [],
                callToAction: descData.callToAction || null,
                review_author: descData.review_author || null,
                review_rating: descData.review_rating || null,
                review_text: descData.review_text || null,
                template_type: descData.template_type || (descData.topicType === 'TESTIMONIAL' ? 'testimonial' :
                  (descData.topicType === 'EVENT' ? 'festival-edm' : null)),
                festival_name: descData.festival_name || null,
                festival_emoji: descData.festival_emoji || null,
                is_from_db: true,
                is_ai_generated: descData.topicType === 'STANDARD' || descData.topicType === 'OFFER' || descData.topicType === 'EVENT',
                is_testimonial: descData.topicType === 'TESTIMONIAL' || descData.template_type === 'testimonial',
                is_festival: descData.template_type === 'festival-edm' || !!descData.festival_name,
                is_gmb_photo: descData.topicType === 'PHOTO'
              };
            });

          cachedPostsRef.current = convertedPosts;
          return convertedPosts;
        }

        cachedPostsRef.current = [];
        return [];
      } finally {
        // MULTI-TAB FIX: Always release lock after API call
        releaseAPILock();
      }
    } catch (e) {
      logger.error('[MonthlyPlan] Failed to load scheduled posts from DB', e);
      releaseAPILock(); // Release lock on error too
      return [];
    }
  }, []);

  const loadCachedPlan = useCallback(() => {
    if (!cacheKey || !manualDataLoaded) return null;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cachedProducts = parsed.manualProducts || [];
        const cachedServices = parsed.manualServices || [];

        const hasTemplatePosts = parsed.plan?.posts?.some(p =>
          p.template_type === 'testimonial' ||
          p.template_type === 'festival-edm' ||
          p.topicType === 'TESTIMONIAL' ||
          p.topicType === 'EVENT'
        );

        if (!hasTemplatePosts) {
          if (!listsMatch(cachedProducts, manualProducts) || !listsMatch(cachedServices, manualServices)) {
            localStorage.removeItem(cacheKey);
            setCacheTime(null);
            return null;
          }
        }

        const cacheDate = new Date(parsed.cachedAt);
        const now = new Date();
        const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
        if (hoursDiff < 24) {
          setCacheTime(cacheDate);
          return parsed.plan;
        }
        localStorage.removeItem(cacheKey);
        setCacheTime(null);
      }
    } catch (e) {
      logger.error('[MonthlyPlan] Failed to load cached plan', e);
    }
    return null;
  }, [cacheKey, manualDataLoaded, manualProducts, manualServices, listsMatch]);

  const savePlanToCache = useCallback((planData, { products, services } = {}) => {
    if (!cacheKey || !planData) return;
    try {
      const now = new Date();

      const cacheSafePlan = {
        ...planData,
        posts: (planData.posts || []).map(post => {
          if (post.generated_image_url && post.generated_image_url.startsWith('data:')) {
            return {
              ...post,
              generated_image_url: post.db_id ? '' : post.generated_image_url.substring(0, 100) + '...truncated',
              _image_truncated: true
            };
          }
          return post;
        })
      };

      const cacheData = {
        plan: cacheSafePlan,
        cachedAt: now.toISOString(),
        manualProducts: products ?? manualProducts,
        manualServices: services ?? manualServices,
        tabId: TAB_ID // MULTI-TAB: Track which tab saved this
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      setCacheTime(now);

      console.log(`[MonthlyPlan Tab ${TAB_ID.substr(-4)}] Saved plan to cache`);
    } catch (e) {
      logger.error('[MonthlyPlan] Failed to cache plan', e);
    }
  }, [cacheKey, manualProducts, manualServices]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Effect 0: Check Payment Access on Mount (FIRST - blocks page until checked)
  useEffect(() => {
    let cancelled = false;

    const checkPaymentAccess = async () => {
      try {
        setAccessChecking(true);

        // Get location_id from defaultProfile to check AI limit for this specific GMB profile
        const locationId = defaultProfile?.locationId
          || defaultProfile?.id
          || defaultProfile?.location_id
          || (defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);

        // Build API URL with optional location_id
        let apiUrl = '/customer/payment/check-monthly-plan-access';
        if (locationId) {
          apiUrl += `?location_id=${encodeURIComponent(locationId)}`;
        }

        const resp = await ApiService.get(apiUrl);

        if (cancelled) return;

        const data = resp?.data || resp || {};
        const hasAccess = data.hasAccess === true;
        const isTrial = data.isTrial === true;

        setHasPaymentAccess(hasAccess);
        setPaymentPlan(data.plan || null);
        setAccessDeniedReason(hasAccess ? null : (data.reason || 'no_payment'));
        setIsTrialUser(isTrial); // Set trial user flag for ₹1 coupon users

        logger.info(`[MonthlyPlan] Access check: hasAccess=${hasAccess}, plan=${data.plan}, isTrial=${isTrial}, reason=${data.reason}, locationId=${locationId}`);
      } catch (err) {
        logger.error('[MonthlyPlan] Failed to check payment access:', err);
        if (!cancelled) {
          // On error, deny access for safety
          setHasPaymentAccess(false);
          setAccessDeniedReason('error');
        }
      } finally {
        if (!cancelled) {
          setAccessChecking(false);
        }
      }
    };

    checkPaymentAccess();

    return () => {
      cancelled = true;
    };
  }, [defaultProfile?.locationId, defaultProfile?.id]); // Re-check when profile changes

  // Effect 1: Sync Default Profile with Plan Context
  useEffect(() => {
    if (defaultProfile) {
      const robustProfile = {
        ...defaultProfile,
        profileKey: defaultProfile.profileKey ||
          (defaultProfile.accountId && defaultProfile.locationId
            ? `${defaultProfile.accountId}:${defaultProfile.id || defaultProfile.locationId}`
            : null)
      };
      selectProfile(robustProfile);
    }
  }, [defaultProfile, selectProfile]);

  // Effect 2: Session change handler
  useEffect(() => {
    const handleSessionChange = (event) => {
      setPlan(null);
      setCacheTime(null);
      setError('');
      setManualDataLoaded(false);
      setManualProducts([]);
      setManualServices([]);
      setProductInput('');
      setServiceInput('');
      setSkipProductServicePrompt(false);
      setStablePlanCode('basic');
      setIsProfileSwitching(false);
      setProfileDialogDismissed(false);
      isGeneratingRef.current = false;
    };

    window.addEventListener('sessionChanged', handleSessionChange);
    return () => {
      window.removeEventListener('sessionChanged', handleSessionChange);
    };
  }, []);

  // Effect 3: Update stable planCode
  useEffect(() => {
    if (planCode && !isPlanLoading) {
      setStablePlanCode(planCode);
    }
  }, [planCode, isPlanLoading]);

  // Effect 4: Load profiles if not present
  useEffect(() => {
    if (!defaultProfile && profiles.length === 0) {
      loadProfiles();
    }
  }, [defaultProfile]);

  // Effect 5: Fetch admin AI post limit
  useEffect(() => {
    let cancelled = false;

    const fetchAdminAiPostLimit = async () => {
      const locationId = defaultProfile?.locationId
        || defaultProfile?.id
        || defaultProfile?.location_id
        || (defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);

      if (!locationId) {
        setAdminAiPostLimit(null);
        return;
      }

      try {
        const resp = await ApiService.get(`/admin/gmb-account/get-ai-post-limit?location_id=${encodeURIComponent(locationId)}`);
        if (!cancelled) {
          const limit = resp?.data?.ai_post_limit || resp?.ai_post_limit || null;
          setAdminAiPostLimit(limit);
        }
      } catch (e) {
        if (!cancelled) {
          setAdminAiPostLimit(null);
        }
      }
    };

    fetchAdminAiPostLimit();

    return () => {
      cancelled = true;
    };
  }, [defaultProfile]);

  // Effect 6: Load products/services
  useEffect(() => {
    let cancelled = false;

    const locationId = defaultProfile?.locationId
      || defaultProfile?.id
      || defaultProfile?.location_id
      || (defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);

    const skipStorageKey = locationId ? `visibeen_skip_product_service_${locationId}` : null;

    if (!locationId) {
      setManualProducts([]);
      setManualServices([]);
      setProductInput('');
      setServiceInput('');
      setSkipProductServicePrompt(false);
      setManualDataLoaded(true);
      return;
    }

    const loadProductsServices = async () => {
      try {
        const resp = await ApiService.get(`/customer/auto-post/products-services/${encodeURIComponent(locationId)}`);
        if (cancelled) return;

        const data = resp?.data || resp || {};
        const storedProducts = Array.isArray(data.products) ? data.products : [];
        const storedServices = Array.isArray(data.services) ? data.services : [];

        setManualProducts(storedProducts);
        setManualServices(storedServices);
        setProductInput(storedProducts.join(', '));
        setServiceInput(storedServices.join(', '));

        const skipPref = skipStorageKey ? localStorage.getItem(skipStorageKey) : null;
        setSkipProductServicePrompt(skipPref === 'true');
      } catch (e) {
        if (cancelled) return;

        setManualProducts([]);
        setManualServices([]);
        setProductInput('');
        setServiceInput('');
        const skipPref = skipStorageKey ? localStorage.getItem(skipStorageKey) : null;
        setSkipProductServicePrompt(skipPref === 'true');
      } finally {
        if (!cancelled) {
          setManualDataLoaded(true);
        }
      }
    };

    loadProductsServices();

    return () => {
      cancelled = true;
    };
  }, [defaultProfile]);

  // Effect 7: ESC key listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (fullImageUrl) setFullImageUrl(null);
        else if (selectedPost) setSelectedPost(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullImageUrl, selectedPost]);

  // Effect 8: Auto-open profile selection dialog
  useEffect(() => {
    if (!defaultProfile && !profilesLoading && profiles.length > 0 && !profileSelectionDialogOpen && !profileDialogDismissed) {
      setProfileSelectionDialogOpen(true);
    }
  }, [defaultProfile, profilesLoading, profiles, profileSelectionDialogOpen, profileDialogDismissed]);

  // Effect 9: Load plan data when profile is ready (and on profile changes)
  useEffect(() => {
    let cancelled = false;

    const loadPlanData = async () => {
      const now = Date.now();
      const timeSinceLastLoad = now - lastLoadTimeRef.current;

      // Prevent rapid calls (debounce 1 second)
      if (isLoadingPlanDataRef.current || timeSinceLastLoad < 1000) {
        return;
      }

      // Wait for profile to be available
      if (!gmbAccountId || !defaultProfile) {
        return;
      }

      // Don't block on subscription context - we now fetch plan directly from API
      // This fixes the issue where subscription check fails due to profileKey format

      lastLoadTimeRef.current = now;
      isLoadingPlanDataRef.current = true;

      try {
        const currentLocationId = defaultProfile?.locationId
          || defaultProfile?.id
          || defaultProfile?.location_id
          || (defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);

        logger.info('[MonthlyPlan] Loading scheduled posts from DB for location:', currentLocationId);
        logger.info('[MonthlyPlan] defaultProfile debug:', {
          hasDefaultProfile: !!defaultProfile,
          locationId: defaultProfile?.locationId,
          id: defaultProfile?.id,
          location_id: defaultProfile?.location_id,
          locationDataName: defaultProfile?.locationData?.name,
          resolvedLocationId: currentLocationId
        });

        const dbPosts = await loadScheduledPostsFromDB(currentLocationId);
        const cachedPlan = loadCachedPlan();
        const currentMaxPosts = getMaxPostsByPlan(stablePlanCode || planCode || 'basic');

        logger.info(`[MonthlyPlan] Loaded ${dbPosts.length} posts from DB, maxPosts: ${currentMaxPosts}`);

        if (dbPosts.length > 0) {
          const needsMorePosts = dbPosts.length < currentMaxPosts;

          if (needsMorePosts) {
            const dbPlan = {
              weekly_summary: 'Scheduled posts from your calendar',
              business_focus: 'Your scheduled content',
              posts: dbPosts,
              cost_optimized: true,
              ai_posts_count: 0,
              template_posts_count: dbPosts.length
            };
            if (!cancelled) {
              setPlan(dbPlan);
              savePlanToCache(dbPlan);
            }
          } else {
            if (cachedPlan) {
              const nonDbPosts = (cachedPlan.posts || []).filter(p => {
                if (p.db_id || p.is_from_db) return false;
                const collision = dbPosts.some(dbP => dbP.day_offset === p.day_offset);
                return !collision;
              });

              const mergedPlan = {
                ...cachedPlan,
                posts: [...dbPosts, ...nonDbPosts]
              };
              if (!cancelled) {
                setPlan(mergedPlan);
                savePlanToCache(mergedPlan);
              }
            } else {
              const dbPlan = {
                weekly_summary: 'Scheduled posts from your calendar',
                business_focus: 'Your scheduled content',
                posts: dbPosts,
                cost_optimized: true,
                ai_posts_count: 0,
                template_posts_count: dbPosts.length
              };
              if (!cancelled) {
                setPlan(dbPlan);
                savePlanToCache(dbPlan);
              }
            }
          }
        } else if (cachedPlan) {
          if (!cancelled) {
            setPlan(cachedPlan);
          }
        } else {
          if (!cancelled) {
            setPlan(null);
          }
        }

        if (!cancelled) {
          setIsProfileSwitching(false);
          setInitialPostsLoaded(true); // Mark initial load as complete
        }
      } finally {
        isLoadingPlanDataRef.current = false;
      }
    };

    // Load immediately when profile becomes available
    if (gmbAccountId && defaultProfile) {
      // Small delay to allow other state to settle
      const timeoutId = setTimeout(() => {
        if (!cancelled) {
          loadPlanData();
        }
      }, 200);

      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [gmbAccountId, defaultProfile?.id, defaultProfile?.locationId]); // Trigger on profile changes

  // Effect 10: MULTI-TAB: Tab heartbeat and cleanup
  useEffect(() => {
    console.log(`[MonthlyPlan] Tab ${TAB_ID.substr(-4)} initialized`);

    updateTabHeartbeat();

    heartbeatIntervalRef.current = setInterval(() => {
      updateTabHeartbeat();
      const isPrimary = isPrimaryTab();
      setIsActiveTab(isPrimary);

      if (!isPrimary) {
        console.log(`[MonthlyPlan Tab ${TAB_ID.substr(-4)}] Secondary tab, limiting activity`);
      }
    }, HEARTBEAT_INTERVAL);

    const handleBeforeUnload = () => {
      cleanupTabResources();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cleanupTabResources();
    };
  }, []);

  // Effect 11: MULTI-TAB: Listen to storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === cacheKey && e.newValue && e.oldValue !== e.newValue) {
        console.log(`[MonthlyPlan Tab ${TAB_ID.substr(-4)}] Cache updated by another tab`);
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.plan) {
            setPlan(parsed.plan);
          }
        } catch (err) {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [cacheKey]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const loadProfiles = async () => {
    setProfilesLoading(true);
    setError('');
    try {
      let collected = [];

      const cachedProfiles = localStorage.getItem('availableGMBProfiles');
      const cachedOwner = localStorage.getItem('availableGMBProfilesOwner');

      let currentOwner = 'anonymous';
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          currentOwner = (user?.email || '').toLowerCase() || 'anonymous';
        }
      } catch (e) { }

      try {
        const adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
        if (adminAccessDataRaw) {
          const adminAccess = JSON.parse(adminAccessDataRaw);
          if (adminAccess?.targetUserId) {
            currentOwner = `admin-view:${adminAccess.targetUserId}`;
          }
        }
      } catch (_) { }

      const ownerMatches = !cachedOwner || String(cachedOwner) === String(currentOwner);
      if (cachedProfiles && ownerMatches) {
        try {
          const profiles = JSON.parse(cachedProfiles);
          if (Array.isArray(profiles) && profiles.length > 0) {
            collected = profiles.map((p) => ({
              id: p.id,
              locationId: p.locationId || p.id,
              accountId: p.accountData?.name?.split('/').pop() || '',
              gmbAccountId: p.id,
              profileKey: `${p.accountData?.name?.split('/').pop() || ''}:${p.locationId || p.id}`,
              name: p.name || p.title || 'Business',
              address: p.address || '',
              locationData: p.locationData || {},
              account: p.accountData
            }));
          }
        } catch (e) {
          // Silent fail
        }
      }

      if (collected.length === 0) {
        let userId = null;
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            userId = user.id || user.user_id;
          }
        } catch (e) { }

        if (userId) {
          try {
            const userResp = await ApiService.get(`/users/get-user/${userId}`);
            const responseData = userResp?.data?.data || userResp?.data || {};
            const gmbAccounts = responseData?.gmb_accounts || [];

            if (gmbAccounts.length > 0) {
              collected = gmbAccounts.map((acct) => ({
                id: acct.id,
                locationId: String(acct.location_id || acct.id || ''),
                accountId: String(acct.account_id || ''),
                gmbAccountId: acct.id,
                profileKey: `${acct.account_id || ''}:${acct.location_id || acct.id || ''}`,
                name: acct.business_name || acct.location_name || 'Business',
                address: acct.location_data?.storefrontAddress?.formattedAddress || '',
                locationData: acct.location_data || {},
                account: { name: `accounts/${acct.account_id}` },
                plan: acct.plan
              }));
            }
          } catch (dbErr) {
            // Silent fail
          }
        }
      }

      if (collected.length === 0) {
        try {
          const accessToken = localStorage.getItem('googleAccessToken');
          if (accessToken) {
            const accounts = await GMBService.getAccounts(accessToken);
            const allLocations = [];

            for (const acct of accounts) {
              if (!acct?.name) continue;
              try {
                const locs = await GMBService.getLocations(accessToken, acct.name);
                if (Array.isArray(locs) && locs.length) {
                  locs.forEach(l => allLocations.push({ account: acct, loc: l }));
                }
              } catch (locErr) {
                // Silent fail
              }
            }

            if (allLocations.length > 0) {
              collected = allLocations.map(({ account, loc }) => ({
                id: loc.name?.split('/').pop(),
                locationId: loc.name?.split('/').pop(),
                accountId: account.name?.split('/').pop() || '',
                gmbAccountId: loc.name?.split('/').pop(),
                profileKey: `${account.name?.split('/').pop() || ''}:${loc.name?.split('/').pop() || ''}`,
                name: loc.title || loc.locationName || 'Business',
                address: loc.storefrontAddress ?
                  `${loc.storefrontAddress.addressLines?.join(', ') || ''}, ${loc.storefrontAddress.locality || ''}`.trim() :
                  'Address not available',
                locationData: loc,
                account: account
              }));

              try {
                localStorage.setItem('availableGMBProfiles', JSON.stringify(collected));
                localStorage.setItem('availableGMBProfilesOwner', String(currentOwner));
              } catch (cacheErr) {
                // Silent fail
              }
            }
          }
        } catch (gmbErr) {
          // Silent fail
        }
      }

      if (collected.length === 0 && defaultProfile) {
        collected = [{
          id: defaultProfile.id,
          locationId: defaultProfile.locationId || defaultProfile.id,
          accountId: defaultProfile.accountId || (defaultProfile.account?.name?.split('/').pop() || ''),
          gmbAccountId: defaultProfile.id || defaultProfile.gmbAccountId,
          profileKey: defaultProfile.profileKey || `${defaultProfile.accountId || ''}:${defaultProfile.locationId || defaultProfile.id || ''}`,
          name: defaultProfile.name || 'Business',
          address: defaultProfile.address || '',
          locationData: defaultProfile.locationData || {},
          account: defaultProfile.account
        }];
      }

      if (collected.length === 0) {
        const hasGoogleToken = !!localStorage.getItem('googleAccessToken');
        if (!hasGoogleToken) {
          setError('Please connect your Google Business account first. Visit the Dashboard to connect.');
        } else {
          setError('No GMB profiles found. Please ensure your Google Business account has locations set up.');
        }
      }

      setProfiles(collected);
      if (collected.length > 0 && !selectedProfileId) {
        setSelectedProfileId(String(collected[0].locationId));
      }

      // AUTO-SELECT DEFAULT PROFILE if not already set
      // Priority: 1. Trial subscription, 2. Active subscription, 3. Single profile, 4. First profile
      if (!defaultProfile && collected.length > 0) {
        logger.log('[MonthlyPlan] No default profile set, auto-selecting...');

        if (collected.length === 1) {
          // Single profile - auto-select it
          logger.log('[MonthlyPlan] Auto-selecting single profile:', collected[0].name);
          setDefaultProfile(collected[0]);
        } else {
          // Multiple profiles - find one with trial/subscription
          try {
            const locationIds = collected.map(p => p.locationId).filter(Boolean);
            const subsResp = await ApiService.post('/customer/subscription/profiles/bulk', { locationIds });
            const subsData = subsResp?.data?.data || subsResp?.data || {};

            let selectedProfile = null;
            for (const profile of collected) {
              const locId = String(profile.locationId);
              const subInfo = subsData[locId] || subsData[`locations/${locId}`];

              if (subInfo && subInfo.hasSubscription) {
                if (subInfo.isTrial) {
                  // Trial has highest priority
                  selectedProfile = profile;
                  logger.log('[MonthlyPlan] Auto-selecting trial profile:', profile.name);
                  break;
                } else if (!selectedProfile) {
                  // Active subscription
                  selectedProfile = profile;
                  logger.log('[MonthlyPlan] Found subscribed profile:', profile.name);
                }
              }
            }

            if (selectedProfile) {
              setDefaultProfile(selectedProfile);
            } else {
              // No subscription found - select first profile
              logger.log('[MonthlyPlan] No subscription found, selecting first profile:', collected[0].name);
              setDefaultProfile(collected[0]);
            }
          } catch (subErr) {
            logger.warn('[MonthlyPlan] Failed to fetch subscriptions for auto-select:', subErr?.message);
            // Fallback to first profile
            setDefaultProfile(collected[0]);
          }
        }
      }
    } catch (e) {
      logger.error('[MonthlyPlan] Failed to load profiles', e);
      setError(e?.message || 'Connect your Google Business Profile to view reports.');
    } finally {
      setProfilesLoading(false);
    }
  };

  const fetchPlan = async (forceRegenerate = false, overrides = {}) => {
    if (!gmbAccountId) {
      setError('Select a business profile to view the monthly plan.');
      return;
    }

    const productsForRequest = (Array.isArray(overrides.manualProducts) ? overrides.manualProducts : manualProducts)
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter(Boolean);
    const servicesForRequest = (Array.isArray(overrides.manualServices) ? overrides.manualServices : manualServices)
      .map((item) => (typeof item === 'string' ? item.trim() : item))
      .filter(Boolean);

    if (!forceRegenerate && productsForRequest.length === 0 && servicesForRequest.length === 0) {
      const cachedPlan = loadCachedPlan();
      if (cachedPlan) {
        setPlan(cachedPlan);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSelectedPost(null);
    try {
      const cleanAccountId = defaultProfile?.account?.name
        ? String(defaultProfile.account.name).split('/').pop()
        : defaultProfile?.accountId;
      const cleanLocationId = defaultProfile?.locationData?.name
        ? String(defaultProfile.locationData.name).split('/').pop()
        : defaultProfile?.locationId;

      // Check if admin is viewing another user's profile
      let targetUserId = null;
      try {
        let adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
        if (!adminAccessDataRaw) {
          const tempKey = localStorage.getItem('latestAdminAccessTempKey');
          if (tempKey) {
            adminAccessDataRaw = localStorage.getItem(tempKey);
          }
        }
        if (adminAccessDataRaw) {
          const adminAccess = JSON.parse(adminAccessDataRaw);
          if (adminAccess?.targetUserId) {
            targetUserId = adminAccess.targetUserId;
            logger.log(`[MonthlyPlan] Admin previewing plan for user ${targetUserId}`);
          }
        }
      } catch (e) {
        // Silent fail
      }

      const resp = await ApiService.post('/customer/auto-post/preview-plan', {
        gmbAccountId,
        accountId: cleanAccountId,
        locationId: cleanLocationId,
        postsPerWeek,
        planVariant: 'weekly-report',
        locationData: defaultProfile?.locationData || null,
        businessName: defaultProfile?.name || null,
        manualProducts: productsForRequest,
        manualServices: servicesForRequest,
        targetUserId // Include when admin is previewing for another user
      });

      const dataPlan = resp?.data?.plan || resp?.plan || null;
      if (!dataPlan) throw new Error('No weekly plan returned');
      setPlan(dataPlan);
      savePlanToCache(dataPlan, { products: productsForRequest, services: servicesForRequest });
    } catch (err) {
      logger.error('[MonthlyPlan] Failed to load plan', err);
      setError(err?.message || 'Failed to load monthly plan');
      setPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (post) => {
    if (!post || !post.db_id) {
      setError('Cannot delete post: No database ID found');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const resp = await ApiService.delete(`/customer/post-scheduler/delete-scheduled-post/${post.db_id}`);

      if (resp) {
        const updatedPosts = (plan?.posts || []).filter(p => p.db_id !== post.db_id);
        const updatedPlan = { ...(plan || {}), posts: updatedPosts };
        setPlan(updatedPlan);
        savePlanToCache(updatedPlan);

        setSelectedPost(null);
        setEditPostDialogOpen(false);

        setScheduleSuccess('Post deleted successfully!');
        setTimeout(() => setScheduleSuccess(null), 3000);
      }
    } catch (err) {
      logger.error('[MonthlyPlan] Failed to delete post:', err);
      setError(err?.message || 'Failed to delete post');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProfile = () => {
    const profile = profiles.find((p) => String(p.locationId) === String(selectedProfileId));
    if (!profile) return;

    setIsProfileSwitching(true);
    setDefaultProfile(profile);
    setPlan(null);
    setCacheTime(null);
    setError('');
    setManualDataLoaded(false);
    setManualProducts([]);
    setManualServices([]);
    setProductInput('');
    setServiceInput('');
    setSkipProductServicePrompt(false);
    setAdminAiPostLimit(null);
    setInitialPostsLoaded(false); // Reset so we wait for new profile's posts to load
    isGeneratingRef.current = false;
    hasLoadedRef.current = false;
  };

  const handleSelectProfileFromDialog = (profile) => {
    setIsProfileSwitching(true);
    setDefaultProfile(profile);
    setProfileSelectionDialogOpen(false);
    setPlan(null);
    setCacheTime(null);
    setError('');
    setManualDataLoaded(false);
    setManualProducts([]);
    setManualServices([]);
    setProductInput('');
    setServiceInput('');
    setAdminAiPostLimit(null);
    setInitialPostsLoaded(false); // Reset so we wait for new profile's posts to load
    isGeneratingRef.current = false;
    setSkipProductServicePrompt(false);
    hasLoadedRef.current = false;
  };

  const handleConnect = () => navigate('/connect-google');

  const handleGeneratePlan = () => {
    if (!gmbAccountId) return;
    if (!skipProductServicePrompt && manualProducts.length === 0 && manualServices.length === 0) {
      setPendingGenerateAfterPrompt(true);
      setProductServiceDialogOpen(true);
      return;
    }
    fetchPlan(true);
  };

  const handleOpenProductServiceDialog = () => {
    setPendingGenerateAfterPrompt(false);
    setProductServiceDialogOpen(true);
  };

  const persistProductServiceValues = async (products, services) => {
    const locationId = defaultProfile?.locationId
      || defaultProfile?.id
      || defaultProfile?.location_id
      || (defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);

    if (!locationId) return;

    try {
      await ApiService.post('/customer/auto-post/products-services', {
        locationId,
        gmbAccountId: gmbAccountId || null,
        products,
        services
      });
    } catch (e) {
      logger.error('[MonthlyPlan] Failed to save product/service data to DB', e);
    }
  };

  const handleSaveProductServices = (generateAfter = false) => {
    const parsedProducts = normalizeListInput(productInput);
    const parsedServices = normalizeListInput(serviceInput);

    setManualProducts(parsedProducts);
    setManualServices(parsedServices);
    persistProductServiceValues(parsedProducts, parsedServices);

    if (skipStorageKey) localStorage.removeItem(skipStorageKey);
    setSkipProductServicePrompt(false);
    setProductServiceDialogOpen(false);

    const shouldGenerate = generateAfter || pendingGenerateAfterPrompt;
    if (shouldGenerate) {
      setPendingGenerateAfterPrompt(false);
      fetchPlan(true, { manualProducts: parsedProducts, manualServices: parsedServices });
    }
  };

  const handleSkipProductServices = (generateAfter = false) => {
    if (skipStorageKey) localStorage.setItem(skipStorageKey, 'true');
    setSkipProductServicePrompt(true);
    setProductServiceDialogOpen(false);

    const shouldGenerate = generateAfter || pendingGenerateAfterPrompt;
    if (shouldGenerate) {
      setPendingGenerateAfterPrompt(false);
      fetchPlan(true);
    }
  };

  const handleCloseProductDialog = () => {
    setProductServiceDialogOpen(false);
    setPendingGenerateAfterPrompt(false);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);

    let scheduledTime = '';
    if (post.db_id && post.scheduledTime) {
      scheduledTime = new Date(post.scheduledTime).toISOString().slice(0, 16);
    } else if (post.day_offset !== undefined && post.time_of_day) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const scheduledDate = new Date(today.getTime() + (post.day_offset * 24 * 60 * 60 * 1000));
      const [hours, minutes] = post.time_of_day.split(':');
      scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      scheduledTime = scheduledDate.toISOString().slice(0, 16);
    }

    setEditFormData({
      title: post.title || '',
      description: post.description || '',
      scheduled_time: scheduledTime,
      post_url: post.generated_image_url || '',
      keywords: Array.isArray(post.keywords) ? post.keywords.join(', ') : '',
      hashtags: Array.isArray(post.hashtags) ? post.hashtags.join(', ') : ''
    });

    setSelectedPost(null);

    setTimeout(() => {
      setEditPostDialogOpen(true);
    }, 100);
  };

  const handleSaveEditPost = async () => {
    if (!editingPost || !editingPost.db_id) {
      setError('Cannot edit post: No database ID found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Parse keywords and hashtags from comma-separated strings to arrays
      const keywordsArray = editFormData.keywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
      const hashtagsArray = editFormData.hashtags
        .split(',')
        .map(h => h.trim())
        .filter(Boolean);

      const updateData = {
        post_name: editFormData.title,
        title: editFormData.title,
        description: editFormData.description,
        post_url: editFormData.post_url,
        scheduled_time: new Date(editFormData.scheduled_time).toISOString(),
        keywords: keywordsArray,
        hashtags: hashtagsArray
      };

      const resp = await ApiService.put(`/customer/post-scheduler/update-scheduled-post/${editingPost.db_id}`, updateData);

      if (resp) {
        const updatedPosts = plan.posts.map(p =>
          p.db_id === editingPost.db_id
            ? { ...p, ...updateData, title: updateData.title, description: updateData.description, keywords: keywordsArray, hashtags: hashtagsArray }
            : p
        );

        const updatedPlan = { ...plan, posts: updatedPosts };
        setPlan(updatedPlan);
        savePlanToCache(updatedPlan);

        setEditPostDialogOpen(false);
        setEditingPost(null);
        setEditFormData({
          title: '',
          description: '',
          scheduled_time: '',
          post_url: '',
          keywords: '',
          hashtags: ''
        });

        setScheduleSuccess('Post updated successfully!');
        setTimeout(() => setScheduleSuccess(null), 3000);
      }
    } catch (err) {
      logger.error('[MonthlyPlan] Failed to update post:', err);
      setError(err?.message || 'Failed to update post');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePlan = async () => {
    if (!plan?.posts || plan.posts.length === 0) {
      setError('No posts to schedule');
      return;
    }

    setScheduling(true);
    setError('');
    setScheduleSuccess(null);

    try {
      const cleanAccountId = defaultProfile?.account?.name
        ? String(defaultProfile.account.name).split('/').pop()
        : defaultProfile?.accountId;
      const cleanLocationId = defaultProfile?.locationData?.name
        ? String(defaultProfile.locationData.name).split('/').pop()
        : defaultProfile?.locationId;

      const resp = await ApiService.post('/customer/auto-post/schedule-plan', {
        gmbAccountId,
        accountId: cleanAccountId,
        locationId: cleanLocationId,
        posts: plan.posts,
        locationData: defaultProfile?.locationData || null,
        businessName: defaultProfile?.name || null
      });

      const data = resp?.data || resp || {};
      setScheduleSuccess({
        count: data.scheduledCount || plan.posts.length,
        posts: data.scheduledPosts || []
      });
    } catch (err) {
      logger.error('[MonthlyPlan] Failed to schedule plan', err);
      setError(err?.message || 'Failed to schedule posts');
    } finally {
      setScheduling(false);
    }
  };

  // Fetch GMB photos from client profile
  const fetchGMBPhotos = useCallback(async () => {
    try {
      // Try multiple sources for account ID
      const accountId = defaultProfile?.account?.name
        ? String(defaultProfile.account.name).replace('accounts/', '')
        : (defaultProfile?.accountId || defaultProfile?.account_id || null);

      // Try multiple sources for location ID
      const locationId = defaultProfile?.locationData?.name
        ? String(defaultProfile.locationData.name).replace('locations/', '')
        : (defaultProfile?.locationId || defaultProfile?.location_id || defaultProfile?.id || null);

      console.log(`[MonthlyPlan] fetchGMBPhotos: accountId=${accountId}, locationId=${locationId}`);

      if (!accountId || !locationId) {
        console.warn('[MonthlyPlan] fetchGMBPhotos: Missing accountId or locationId, returning empty array');
        return [];
      }

      // Get access token from localStorage for GMBService
      let googleAccessToken = null;
      try {
        googleAccessToken = localStorage.getItem('googleAccessToken');
        if (!googleAccessToken) {
          console.warn('[MonthlyPlan] fetchGMBPhotos: No Google access token in localStorage');
        }
      } catch (e) {
        console.warn('[MonthlyPlan] fetchGMBPhotos: Failed to get Google access token:', e.message);
      }

      const media = await GMBService.getMedia(googleAccessToken, accountId, locationId);
      console.log(`[MonthlyPlan] fetchGMBPhotos: Got ${media?.length || 0} media items from GMB`);

      const photos = (media || [])
        .filter(m => m.mediaFormat === 'PHOTO' && m.googleUrl)
        .map(m => ({
          url: m.googleUrl,
          category: m.locationAssociation?.category || 'ADDITIONAL'
        }))
        .slice(0, 20); // Increased limit to support larger plans (Pro = 20 posts)

      console.log(`[MonthlyPlan] fetchGMBPhotos: Filtered to ${photos.length} usable photos`);
      return photos;
    } catch (err) {
      console.error('[MonthlyPlan] fetchGMBPhotos ERROR:', err?.message || err);
      console.error('[MonthlyPlan] fetchGMBPhotos ERROR details:', err);
      return [];
    }
  }, [defaultProfile]);

  // Create a testimonial post from 5-star reviews
  const handlePostFromTestimonial = useCallback(async () => {
    if (!defaultProfile) {
      setError('Please select a business profile first');
      return;
    }

    setTestimonialLoading(true);
    setError('');

    try {
      const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
      const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');

      const accountName = account?.name || defaultProfile?.account?.name;
      const locationName = location?.name || defaultProfile?.locationData?.name;

      if (!accountName || !locationName) {
        setError('Please connect a GMB account to fetch reviews');
        return;
      }

      const reviewsResponse = await GMBService.getReviews(undefined, accountName, locationName);
      const reviewsArray = Array.isArray(reviewsResponse) ? reviewsResponse : (reviewsResponse?.reviews || []);

      const fiveStarReviews = reviewsArray.filter(r => {
        const rating = r.starRating;
        const isFiveStar = rating === 'FIVE' || rating === 5;
        const hasText = r.comment && r.comment.trim().length > 0;
        return isFiveStar && hasText;
      });

      if (fiveStarReviews.length === 0) {
        setError('No 5-star reviews with text found. Please collect some reviews first.');
        return;
      }

      const reviewWithMaxWords = fiveStarReviews.reduce((max, review) => {
        const currentWords = (review.comment || '').trim().split(/\s+/).length;
        const maxWords = (max.comment || '').trim().split(/\s+/).length;
        return currentWords > maxWords ? review : max;
      }, fiveStarReviews[0]);

      const authorName = reviewWithMaxWords.reviewer?.displayName || 'Happy Customer';
      const reviewText = reviewWithMaxWords.comment || '';
      const avatarUrl = reviewWithMaxWords.reviewer?.profilePhotoUrl || 'https://i.pravatar.cc/150?img=5';
      const businessName = defaultProfile?.name || 'Our Business';

      const postData = {
        title: "Customer",
        subtitle: "Testimonial",
        userProfile: {
          name: authorName,
          title: 'Verified Customer',
          avatar: avatarUrl
        },
        testimonialText: reviewText,
        rating: 5,
        ctaText: 'Experience Excellence'
      };

      setTestimonialPreviewData(postData);

      await new Promise(resolve => setTimeout(resolve, 500));

      let generatedImageUrl = 'https://img.freepik.com/free-psd/testimonial-template-design_23-2149526336.jpg';

      if (testimonialPreviewRef.current) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(testimonialPreviewRef.current, {
            backgroundColor: '#f8fafc',
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true
          });

          const base64Image = canvas.toDataURL('image/png', 1.0);

          try {
            const uploadResp = await ApiService.post('/customer/auto-post/upload-image', {
              imageData: base64Image,
              filename: `testimonial_${Date.now()}`
            });
            if (uploadResp?.data?.imageUrl) {
              const serverUrl = window.location.hostname === 'localhost'
                ? 'http://localhost:5000'
                : 'https://api.visibeen.com';
              generatedImageUrl = `${serverUrl}${uploadResp.data.imageUrl}`;
            }
          } catch (uploadErr) {
            generatedImageUrl = base64Image;
          }
        } catch (captureErr) {
          logger.warn('[MonthlyPlan] Could not capture template:', captureErr.message);
        }
      }

      setTestimonialPreviewData(null);

      // Generate AI description for the testimonial post
      let aiDescription = `"${reviewText}"\n\n— ${authorName} ⭐⭐⭐⭐⭐\n\nThank you ${authorName} for your wonderful 5-star review! We truly appreciate your kind words.\n\nExperience the same excellent service at ${businessName}!`;
      let aiCaption = `Our customers love us! ⭐⭐⭐⭐⭐ Read what ${authorName} has to say.`;
      let aiKeywords = ['customer review', 'testimonial', '5 star', 'happy customers', businessName.toLowerCase()];
      let aiHashtags = ['#CustomerReview', '#5StarService', '#HappyCustomers', '#Testimonial', '#LocalBusiness'];

      try {
        const aiContent = await generateAIPostDescription('review', {
          businessName: businessName,
          category: defaultProfile?.locationData?.primaryCategory?.displayName || '',
          city: defaultProfile?.locationData?.storefrontAddress?.locality || '',
          reviewerName: authorName,
          rating: 5,
          reviewText: reviewText
        });

        if (aiContent) {
          aiDescription = aiContent.description || aiDescription;
          aiCaption = aiContent.caption || aiCaption;
          if (aiContent.keywords?.length > 0) aiKeywords = aiContent.keywords;
          if (aiContent.hashtags?.length > 0) aiHashtags = aiContent.hashtags;
        }
      } catch (aiErr) {
        logger.warn('[MonthlyPlan] AI testimonial description failed, using default:', aiErr?.message);
      }

      const testimonialPost = {
        day_offset: 2,
        time_of_day: '12:00',
        title: `⭐⭐⭐⭐⭐ Customer Review`,
        description: formatDescriptionWithTags(aiDescription, aiKeywords, aiHashtags),
        caption: aiCaption,
        keywords: aiKeywords,
        hashtags: aiHashtags,
        topicType: 'TESTIMONIAL',
        review_author: authorName,
        review_rating: 5,
        review_text: reviewText,
        callToAction: { actionType: 'LEARN_MORE' },
        generated_image_url: generatedImageUrl,
        is_template: true,
        template_type: 'testimonial',
        template_id: 'template-1',
        userProfile: postData.userProfile,
        testimonialText: reviewText,
        rating: 5
      };

      const cleanAccountId = defaultProfile?.account?.name
        ? String(defaultProfile.account.name).split('/').pop()
        : defaultProfile?.accountId;
      const cleanLocationId = defaultProfile?.locationData?.name
        ? String(defaultProfile.locationData.name).split('/').pop()
        : defaultProfile?.locationId;

      try {
        const scheduleResp = await ApiService.post('/customer/auto-post/schedule-plan', {
          gmbAccountId,
          accountId: cleanAccountId,
          locationId: cleanLocationId,
          posts: [testimonialPost],
          locationData: defaultProfile?.locationData || null,
          businessName: businessName
        });

        const scheduleData = scheduleResp?.data || scheduleResp || {};
        if (scheduleData.scheduledPosts && scheduleData.scheduledPosts.length > 0) {
          testimonialPost.db_id = scheduleData.scheduledPosts[0].id;
          testimonialPost.scheduledTime = scheduleData.scheduledPosts[0].scheduledTime;
        }
      } catch (scheduleErr) {
        logger.warn('[MonthlyPlan] Could not save testimonial to database:', scheduleErr.message);
      }

      if (plan && plan.posts) {
        const updatedPlan = {
          ...plan,
          posts: [...plan.posts, testimonialPost],
          weekly_summary: `${plan.weekly_summary} + Testimonial post added`
        };
        setPlan(updatedPlan);
        savePlanToCache(updatedPlan);
      } else {
        const newPlan = {
          weekly_summary: 'Testimonial post from 5-star review',
          business_focus: 'Customer reviews and testimonials',
          posts: [testimonialPost],
          cost_optimized: true,
          ai_posts_count: 0,
          template_posts_count: 1
        };
        setPlan(newPlan);
        savePlanToCache(newPlan);
      }
    } catch (err) {
      logger.error('[MonthlyPlan] Failed to create testimonial post:', err);
      setError(err?.message || 'Failed to create testimonial post from reviews');
      setTestimonialPreviewData(null);
    } finally {
      setTestimonialLoading(false);
    }
  }, [defaultProfile, gmbAccountId, plan, savePlanToCache]);

  // Create a festival EDM post
  const handlePostFromEDM = useCallback(async () => {
    if (!defaultProfile) {
      setError('Please select a business profile first');
      return;
    }

    setEdmLoading(true);
    setError('');

    try {
      const businessName = defaultProfile?.name || 'Our Business';
      const businessAddress = defaultProfile?.address || defaultProfile?.locationData?.storefrontAddress?.formattedAddress || '';
      const businessPhone = defaultProfile?.locationData?.phoneNumbers?.primaryPhone || '';
      const businessWebsite = defaultProfile?.locationData?.websiteUri || '';

      let selectedFestival = null;
      let festivalImage = null;

      try {
        const upcomingResp = await ApiService.get('/customer/festival/get-upcoming-festivals?days=30');
        const upcomingList = Array.isArray(upcomingResp?.data)
          ? upcomingResp.data
          : Array.isArray(upcomingResp)
            ? upcomingResp
            : [];

        if (upcomingList.length > 0) {
          const festival = upcomingList[0];
          const festivalImages = festival.images || [];

          if (festivalImages.length > 0) {
            const randomImageIndex = Math.floor(Math.random() * festivalImages.length);
            festivalImage = festivalImages[randomImageIndex];
          }

          selectedFestival = {
            name: festival.name,
            emoji: festival.emoji || '🎉',
            template: festival.template || festival.name.toLowerCase().replace(/\s/g, '-'),
            images: festivalImages
          };
        }
      } catch (apiErr) {
        logger.warn('[MonthlyPlan] Could not fetch upcoming festivals from API:', apiErr.message);
      }

      if (!selectedFestival) {
        setError('No upcoming festival found. Please add festivals with upcoming dates in Admin Dashboard → Festivals.');
        setEdmLoading(false);
        return;
      }

      if (!festivalImage && (!selectedFestival.images || selectedFestival.images.length === 0)) {
        setError(`Festival "${selectedFestival.name}" has no images. Please add images in Admin Dashboard → Festivals.`);
        setEdmLoading(false);
        return;
      }

      if (!festivalImage && selectedFestival.images && selectedFestival.images.length > 0) {
        festivalImage = selectedFestival.images[0];
      }

      const festivalName = selectedFestival.name;
      const festivalEmoji = selectedFestival.emoji;

      setEdmPreviewData({
        festivalName,
        festivalEmoji,
        festivalImage,
        businessName,
        businessAddress,
        businessPhone,
        businessWebsite,
        ctaText: 'Visit Us Today',
        footerDesign: Math.floor(Math.random() * 3) + 1
      });

      await new Promise(resolve => setTimeout(resolve, 800));

      let generatedImageUrl = festivalImage;

      if (edmPreviewRef.current) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(edmPreviewRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true
          });

          const base64Image = canvas.toDataURL('image/png', 1.0);

          try {
            const uploadResp = await ApiService.post('/customer/auto-post/upload-image', {
              imageData: base64Image,
              filename: `edm_${festivalName}_${Date.now()}`
            });
            if (uploadResp?.data?.imageUrl) {
              const serverUrl = window.location.hostname === 'localhost'
                ? 'http://localhost:5000'
                : 'https://api.visibeen.com';
              generatedImageUrl = `${serverUrl}${uploadResp.data.imageUrl}`;
            }
          } catch (uploadErr) {
            logger.warn('[MonthlyPlan] Could not upload EDM image:', uploadErr.message);
          }
        } catch (captureErr) {
          logger.warn('[MonthlyPlan] Could not capture EDM template:', captureErr.message);
        }
      }

      setEdmPreviewData(null);

      // Generate AI description for the festival EDM post
      let aiDescription = `${festivalEmoji} Happy ${festivalName}! ${festivalEmoji}\n\nWishing you and your loved ones joy, happiness, and prosperity on this special occasion!\n\nFrom all of us at ${businessName}, we hope this ${festivalName} brings you wonderful moments and cherished memories.`;
      let aiCaption = `Celebrating ${festivalName} with you! ${festivalEmoji}`;
      let aiKeywords = [festivalName.toLowerCase(), 'celebration', 'holiday', 'festival', businessName.toLowerCase()];
      let aiHashtags = [`#${festivalName.replace(/[\s']/g, '')}`, '#Celebration', '#HappyHolidays', '#LocalBusiness', '#FestiveVibes'];

      try {
        const aiContent = await generateAIPostDescription('festival', {
          festivalName: festivalName,
          businessName: businessName,
          category: defaultProfile?.locationData?.primaryCategory?.displayName || '',
          city: defaultProfile?.locationData?.storefrontAddress?.locality || ''
        });

        if (aiContent) {
          aiDescription = aiContent.description || aiDescription;
          aiCaption = aiContent.caption || aiCaption;
          if (aiContent.keywords?.length > 0) aiKeywords = aiContent.keywords;
          if (aiContent.hashtags?.length > 0) aiHashtags = aiContent.hashtags;
        }
      } catch (aiErr) {
        logger.warn('[MonthlyPlan] AI festival description failed, using default:', aiErr?.message);
      }

      const edmPost = {
        day_offset: 4,
        time_of_day: '10:00',
        title: `${festivalEmoji} Happy ${festivalName}!`,
        description: formatDescriptionWithTags(aiDescription, aiKeywords, aiHashtags),
        caption: aiCaption,
        keywords: aiKeywords,
        hashtags: aiHashtags,
        topicType: 'EVENT',
        callToAction: { actionType: 'LEARN_MORE' },
        generated_image_url: generatedImageUrl,
        is_template: true,
        template_type: 'festival-edm',
        template_id: selectedFestival.template,
        festival_name: festivalName,
        festival_emoji: festivalEmoji
      };

      const cleanAccountId = defaultProfile?.account?.name
        ? String(defaultProfile.account.name).split('/').pop()
        : defaultProfile?.accountId;
      const cleanLocationId = defaultProfile?.locationData?.name
        ? String(defaultProfile.locationData.name).split('/').pop()
        : defaultProfile?.locationId;

      try {
        const scheduleResp = await ApiService.post('/customer/auto-post/schedule-plan', {
          gmbAccountId,
          accountId: cleanAccountId,
          locationId: cleanLocationId,
          posts: [edmPost],
          locationData: defaultProfile?.locationData || null,
          businessName: businessName
        });

        const scheduleData = scheduleResp?.data || scheduleResp || {};
        if (scheduleData.scheduledPosts && scheduleData.scheduledPosts.length > 0) {
          edmPost.db_id = scheduleData.scheduledPosts[0].id;
          edmPost.scheduledTime = scheduleData.scheduledPosts[0].scheduledTime;
        }
      } catch (scheduleErr) {
        logger.warn('[MonthlyPlan] Could not save festival EDM to database:', scheduleErr.message);
      }

      if (plan && plan.posts) {
        const updatedPlan = {
          ...plan,
          posts: [...plan.posts, edmPost],
          weekly_summary: `${plan.weekly_summary} + Festival EDM post added`
        };
        setPlan(updatedPlan);
        savePlanToCache(updatedPlan);
      } else {
        const newPlan = {
          weekly_summary: `Festival EDM post: ${festivalName}`,
          business_focus: 'Festival celebration and engagement',
          posts: [edmPost],
          cost_optimized: true,
          ai_posts_count: 0,
          template_posts_count: 1
        };
        setPlan(newPlan);
        savePlanToCache(newPlan);
      }
    } catch (err) {
      logger.error('[MonthlyPlan] Failed to create festival EDM post:', err);
      setError(err?.message || 'Failed to create festival EDM post');
      setEdmPreviewData(null);
    } finally {
      setEdmLoading(false);
    }
  }, [defaultProfile, gmbAccountId, plan, savePlanToCache]);

  // Auto Generate Month: Creates posts based on plan limits with smart distribution
  const handleAutoGenerateMonth = useCallback(async () => {
    if (isGeneratingRef.current) {
      return;
    }

    if (!defaultProfile) {
      setError('Please select a business profile first');
      return;
    }

    // SAFEGUARD: Prevent generation if posts haven't been loaded yet
    // This prevents accidental double-generation when page is still loading
    if (!initialPostsLoaded) {
      console.log('[MonthlyPlan] Generation blocked: Still loading existing posts from database');
      setError('Please wait while we check your existing posts...');
      return;
    }

    // SAFEGUARD: Prevent generation if user is on cooldown (has posts scheduled within 30 days)
    if (!generationStatus.canGenerate) {
      console.log(`[MonthlyPlan] Generation blocked: ${generationStatus.upcomingPostsCount} posts scheduled, ${generationStatus.daysRemaining} days remaining`);
      setError(`You have ${generationStatus.upcomingPostsCount} posts scheduled. New generation available in ${generationStatus.daysRemaining} days.`);
      return;
    }

    // DEBUG: Log profile structure to understand available fields
    console.log('[MonthlyPlan] defaultProfile structure:', {
      id: defaultProfile?.id,
      accountId: defaultProfile?.accountId,
      account_id: defaultProfile?.account_id,
      locationId: defaultProfile?.locationId,
      location_id: defaultProfile?.location_id,
      'account.name': defaultProfile?.account?.name,
      'locationData.name': defaultProfile?.locationData?.name,
      name: defaultProfile?.name
    });

    // Initialize generation state
    isGeneratingRef.current = true;
    setCombinedLoading(true);
    setError('');
    setPlan(null);
    setGenerationComplete(false);
    setGenerationResult(null);
    setShowGenerationModal(true);
    setGenerationProgress({
      isGenerating: true,
      currentPhase: 'Initializing...',
      phaseNumber: 0,
      totalPhases: 5,
      postsGenerated: 0,
      targetPosts: 0,
      startTime: Date.now(),
      estimatedTimeRemaining: 60,
      phases: [
        { id: 1, name: 'Preparing', status: 'active', icon: '⚙️' },
        { id: 2, name: 'Festival Posts', status: 'pending', icon: '🎉' },
        { id: 3, name: 'AI Posts', status: 'pending', icon: '🤖' },
        { id: 4, name: 'Review Posts', status: 'pending', icon: '⭐' },
        { id: 5, name: 'Photo Posts', status: 'pending', icon: '📸' }
      ]
    });

    try {
      updateGenerationProgress(1, 'Preparing...', 'active');

      const cleanAccountId = defaultProfile?.account?.name ? String(defaultProfile.account.name).split('/').pop() : defaultProfile?.accountId;
      const cleanLocationId = defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : defaultProfile?.locationId;
      const gmbAccountId = defaultProfile?.id;

      // CRITICAL: Fetch plan and AI post limit directly from GmbAccount
      // This bypasses the subscription context which may have format mismatches
      let freshAdminAiPostLimit = null;
      let fetchedPlan = null; // Plan directly from GmbAccount.plan column
      const locationIdForLimit = defaultProfile?.locationId
        || defaultProfile?.id
        || defaultProfile?.location_id
        || (defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : null);

      if (locationIdForLimit) {
        try {
          const limitResp = await ApiService.get(`/admin/gmb-account/get-ai-post-limit?location_id=${encodeURIComponent(locationIdForLimit)}`);
          // Use ?? to properly handle 0 values (0 means no AI posts, but reviews/photos should still generate)
          freshAdminAiPostLimit = limitResp?.data?.ai_post_limit ?? limitResp?.ai_post_limit ?? null;
          fetchedPlan = limitResp?.data?.plan ?? limitResp?.plan ?? null;

          logger.info(`[MonthlyPlan] Fetched AI post limit: ${freshAdminAiPostLimit}, plan: ${fetchedPlan}`);

          if (fetchedPlan) {
            logger.info(`[MonthlyPlan] Fetched plan from GmbAccount: ${fetchedPlan}`);
          }
        } catch (e) {
          logger.warn('[MonthlyPlan] Failed to fetch AI post limit/plan:', e.message);
        }
      }

      // Use fetched plan from GmbAccount (priority), then context planCode, then stablePlanCode, default to basic
      const effectivePlanCode = fetchedPlan || planCode || stablePlanCode || 'basic';
      const maxPosts = getMaxPostsByPlan(effectivePlanCode);

      logger.info(`[MonthlyPlan] Using plan: ${effectivePlanCode} (fetched: ${fetchedPlan || 'null'}, context: ${planCode || 'null'}) -> maxPosts: ${maxPosts}`);

      const businessInfo = {
        businessName: defaultProfile?.name || 'Our Business',
        businessAddress: defaultProfile?.address || defaultProfile?.locationData?.storefrontAddress?.formattedAddress || '',
        businessPhone: defaultProfile?.locationData?.phoneNumbers?.primaryPhone || '',
        businessWebsite: defaultProfile?.locationData?.websiteUri || ''
      };

      // TRIAL USER LOGIC: For ₹1 trial coupon users
      // - Generate posts for 7 days instead of 30
      // - Generate exactly 4 posts: 2 AI, 1 review, 1 GMB photo
      // - Auto-enable AI limit to 2
      const TRIAL_GENERATION_DAYS = 7;
      const TRIAL_MAX_POSTS = 4;
      const TRIAL_AI_POSTS = 2;
      const TRIAL_REVIEW_POSTS = 1;
      const TRIAL_GMB_PHOTO_POSTS = 1;

      // Determine if trial user and set generation parameters accordingly
      const isTrialGeneration = isTrialUser;
      const GENERATION_PERIOD_DAYS = isTrialGeneration ? TRIAL_GENERATION_DAYS : 30;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const daysRemaining = GENERATION_PERIOD_DAYS;

      const allPosts = [];
      const takenDays = new Set();

      // For trial users, use fixed 4 posts; for regular users, use plan-based max
      const limitedMaxPosts = isTrialGeneration
        ? Math.min(TRIAL_MAX_POSTS, daysRemaining)
        : Math.min(maxPosts, daysRemaining);

      logger.info(`[MonthlyPlan] ${isTrialGeneration ? 'TRIAL USER: ' : ''}Generating ${limitedMaxPosts} posts for the next ${GENERATION_PERIOD_DAYS} days from today`);

      const calculateDayOffset = (targetDate) => {
        const target = new Date(targetDate);
        target.setHours(0, 0, 0, 0);
        const base = new Date(today);
        base.setHours(0, 0, 0, 0);
        return Math.round((target - base) / (1000 * 60 * 60 * 24));
      };

      let existingDbPostsCount = 0;
      try {
        const dbPosts = await loadScheduledPostsFromDB(
          defaultProfile?.locationData?.name ? String(defaultProfile.locationData.name).split('/').pop() : defaultProfile?.locationId
        );
        dbPosts.forEach(p => {
          if (p.day_offset >= 0) {
            takenDays.add(p.day_offset);
            existingDbPostsCount++;
          }
        });

        if (existingDbPostsCount >= limitedMaxPosts) {
          setCombinedLoading(false);
          isGeneratingRef.current = false;
          return;
        }
      } catch (e) {
        logger.warn('Failed to pre-fill taken days from DB', e);
      }

      // PHASE 1: FESTIVALS (Skip for trial users - they get specific post types)
      updateGenerationProgress(2, 'Generating Festival Posts...', 'active');
      if (!isTrialGeneration) {
        try {
          const festivalsResp = await ApiService.get('/customer/festival/get-upcoming-festivals?days=30');
          const festivalsList = Array.isArray(festivalsResp?.data) ? festivalsResp.data : (Array.isArray(festivalsResp) ? festivalsResp : []);

          // Filter festivals that fall within the next 30 days
          const endDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
          const upcomingFestivals = festivalsList.filter(f => {
            const fDate = new Date(f.date);
            return fDate >= today && fDate <= endDate;
          });

          for (const festival of upcomingFestivals) {
            const dayOffset = calculateDayOffset(festival.date);

            if (dayOffset >= 0 && !takenDays.has(dayOffset)) {
              const festImages = festival.images || [];
              const festivalImage = festImages.length > 0 ? festImages[Math.floor(Math.random() * festImages.length)] : null;

              if (festivalImage) {
                setEdmPreviewData({
                  festivalName: festival.name,
                  festivalEmoji: festival.emoji,
                  festivalImage,
                  businessName: businessInfo.businessName,
                  businessAddress: businessInfo.businessAddress,
                  businessPhone: businessInfo.businessPhone,
                  businessWebsite: businessInfo.businessWebsite,
                  ctaText: 'Visit Us Today',
                  footerDesign: (allPosts.length % 3) + 1
                });

                await new Promise(r => setTimeout(r, 800));

                let imageUrl = festivalImage;

                if (edmPreviewRef.current) {
                  try {
                    const html2canvas = (await import('html2canvas')).default;
                    const canvas = await html2canvas(edmPreviewRef.current, {
                      backgroundColor: '#ffffff',
                      scale: 2,
                      useCORS: true,
                      logging: false,
                      allowTaint: true
                    });

                    const base64Image = canvas.toDataURL('image/png', 1.0);

                    const uploadResp = await ApiService.post('/customer/auto-post/upload-image', {
                      imageData: base64Image,
                      filename: `edm_${festival.name.replace(/\s+/g, '_')}_${Date.now()}`
                    });

                    if (uploadResp?.data?.imageUrl) {
                      const serverUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.visibeen.com';
                      imageUrl = `${serverUrl}${uploadResp.data.imageUrl}`;
                    }
                  } catch (err) {
                    logger.warn('Failed to generate festival EDM:', err);
                  }
                }

                setEdmPreviewData(null);

                // Generate AI description for festival post
                let festivalDescription = `Wishing you a wonderful ${festival.name} from ${businessInfo.businessName}!`;
                let festivalKeywords = [festival.name.toLowerCase(), 'celebration', 'festival'];
                let festivalHashtags = [`#${festival.name.replace(/\s+/g, '')}`, '#Celebration', '#HappyHolidays'];
                let festivalCaption = `Celebrating ${festival.name} with you!`;

                try {
                  const aiContent = await generateAIPostDescription('festival', {
                    festivalName: festival.name,
                    businessName: businessInfo.businessName,
                    category: businessInfo.businessCategory || defaultProfile?.locationData?.primaryCategory?.displayName || '',
                    city: businessInfo.businessCity || defaultProfile?.locationData?.storefrontAddress?.locality || ''
                  });

                  if (aiContent) {
                    festivalDescription = aiContent.description || festivalDescription;
                    festivalCaption = aiContent.caption || festivalCaption;
                    if (aiContent.keywords?.length > 0) festivalKeywords = aiContent.keywords;
                    if (aiContent.hashtags?.length > 0) festivalHashtags = aiContent.hashtags;
                  }
                } catch (aiErr) {
                  logger.warn('[MonthlyPlan] AI festival description failed, using default:', aiErr?.message);
                }

                allPosts.push({
                  day_offset: dayOffset,
                  time_of_day: '10:00',
                  title: `${festival.emoji || '🎉'} Happy ${festival.name}!`,
                  description: formatDescriptionWithTags(festivalDescription, festivalKeywords, festivalHashtags),
                  caption: festivalCaption,
                  topicType: 'EVENT',
                  template_type: 'festival-edm',
                  festival_name: festival.name,
                  festival_emoji: festival.emoji,
                  generated_image_url: imageUrl,
                  is_festival: true,
                  keywords: festivalKeywords,
                  hashtags: festivalHashtags
                });
                takenDays.add(dayOffset);
              }
            }
          }
        } catch (e) {
          logger.warn('[MonthlyPlan] Error fetching/generating festivals:', e);
        }
      }

      const festivalPostsCount = allPosts.length;

      // Calculate remaining slots
      let remainingSlots = limitedMaxPosts - festivalPostsCount - existingDbPostsCount;

      if (remainingSlots > 0) {
        // TRIAL USER: Fixed distribution - 2 AI, 1 review, 1 GMB photo
        // REGULAR USER: AI posts only if admin approved, then split remaining between review and GMB
        let effectiveAIPostTarget;
        let targetReviewPosts;
        let targetGMBPhotoPosts;

        // Track actual posts added in each phase for redistribution
        let actualAIPostsAdded = 0;
        let actualReviewPostsAdded = 0;
        let actualPhotoPostsAdded = 0;

        if (isTrialGeneration) {
          // Trial users get exactly: 2 AI posts, 1 review post, 1 GMB photo post
          // For trial, we auto-enable AI (limit = 2)
          effectiveAIPostTarget = Math.min(TRIAL_AI_POSTS, remainingSlots);
          const slotsAfterAI = remainingSlots - effectiveAIPostTarget;
          targetReviewPosts = Math.min(TRIAL_REVIEW_POSTS, slotsAfterAI);
          targetGMBPhotoPosts = Math.min(TRIAL_GMB_PHOTO_POSTS, slotsAfterAI - targetReviewPosts);

          logger.info(`[MonthlyPlan] TRIAL USER post distribution: AI=${effectiveAIPostTarget}, Review=${targetReviewPosts}, GMB Photo=${targetGMBPhotoPosts}`);
        } else {
          // Regular users: AI posts limited by admin setting
          // IMPORTANT: AI limit controls ONLY AI posts, remaining slots go to reviews and GMB photos
          // Example: Pro plan (20 posts), AI limit = 2 → 2 AI + 9 reviews + 9 GMB photos = 20 total
          // If AI limit = 0, ALL slots go to reviews, GMB photos, and festivals (no AI posts)

          // Get the AI post limit - parse as integer to handle string values from API
          // If null/undefined, default to 2 AI posts. If 0, generate NO AI posts.
          let aiLimit = 2; // default
          if (freshAdminAiPostLimit !== null && freshAdminAiPostLimit !== undefined) {
            const parsed = parseInt(freshAdminAiPostLimit, 10);
            if (!Number.isNaN(parsed) && parsed >= 0) {
              aiLimit = parsed;
            }
          }

          logger.info(`[MonthlyPlan] AI limit resolved: ${aiLimit} (raw: ${freshAdminAiPostLimit})`);

          // AI posts get exactly the admin limit (capped by remaining slots)
          // If aiLimit = 0, effectiveAIPostTarget = 0 → all slots go to reviews/photos
          effectiveAIPostTarget = Math.min(aiLimit, remainingSlots);

          // Remaining slots after AI go to reviews and GMB photos (50/50 split)
          const slotsAfterAI = remainingSlots - effectiveAIPostTarget;
          targetReviewPosts = Math.ceil(slotsAfterAI * 0.5);
          targetGMBPhotoPosts = slotsAfterAI - targetReviewPosts;

          logger.info(`[MonthlyPlan] REGULAR USER post distribution: AI limit=${aiLimit}, effectiveAI=${effectiveAIPostTarget}, Reviews=${targetReviewPosts}, GMB Photos=${targetGMBPhotoPosts}, Total=${effectiveAIPostTarget + targetReviewPosts + targetGMBPhotoPosts}`);
        }

        // PRE-CALCULATE DISTRIBUTED DAYS FOR ALL REMAINING POSTS
        // This ensures posts are spread evenly over 30 days instead of consecutive
        const totalRemainingPosts = effectiveAIPostTarget + targetReviewPosts + targetGMBPhotoPosts;
        const distributedDayOffsets = calculateDistributedDays(totalRemainingPosts, daysRemaining, new Set(takenDays));
        let distributedDayIndex = 0;

        // Helper function to get next distributed day
        const getNextDistributedDay = () => {
          if (distributedDayIndex < distributedDayOffsets.length) {
            const day = distributedDayOffsets[distributedDayIndex];
            distributedDayIndex++;
            takenDays.add(day);
            return day;
          }
          // Fallback to next available day if we run out of pre-calculated days
          let fallbackDay = 0;
          while (takenDays.has(fallbackDay)) fallbackDay++;
          takenDays.add(fallbackDay);
          return fallbackDay;
        };

        // PHASE 2: AI POSTS
        updateGenerationProgress(3, 'Generating AI Posts...', 'active', allPosts.length);
        console.log(`[MonthlyPlan] PHASE 2: effectiveAIPostTarget=${effectiveAIPostTarget}, targetReviewPosts=${targetReviewPosts}, targetGMBPhotoPosts=${targetGMBPhotoPosts}`);
        if (effectiveAIPostTarget > 0) {
          try {
            // Get targetUserId for admin access
            let targetUserIdForAI = null;
            try {
              let adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
              if (!adminAccessDataRaw) {
                const tempKey = localStorage.getItem('latestAdminAccessTempKey');
                if (tempKey) {
                  adminAccessDataRaw = localStorage.getItem(tempKey);
                }
              }
              if (adminAccessDataRaw) {
                const adminAccess = JSON.parse(adminAccessDataRaw);
                if (adminAccess?.targetUserId) {
                  targetUserIdForAI = adminAccess.targetUserId;
                }
              }
            } catch (e) { /* Silent */ }

            const resp = await ApiService.post('/customer/auto-post/preview-plan', {
              gmbAccountId,
              accountId: cleanAccountId,
              locationId: cleanLocationId,
              postsPerWeek: effectiveAIPostTarget,
              planVariant: 'monthly-report',
              locationData: defaultProfile?.locationData || null,
              businessName: businessInfo.businessName,
              manualProducts,
              manualServices,
              targetUserId: targetUserIdForAI
            });

            const aiPlan = resp?.data?.plan || resp?.plan;
            const aiPosts = aiPlan?.posts || [];
            const aiError = aiPlan?.ai_error || null;

            // Check if AI had an error (quota exceeded, etc.)
            if (aiError) {
              console.warn(`[MonthlyPlan] PHASE 2: AI returned error: ${aiError}`);
            }

            const filteredAiPosts = aiPosts.filter(post => post.topicType !== 'OFFER');
            console.log(`[MonthlyPlan] PHASE 2: Received ${aiPosts.length} AI posts from API, filtered to ${filteredAiPosts.length}`);

            for (const post of filteredAiPosts) {
              if (actualAIPostsAdded >= effectiveAIPostTarget) break;

              const dayOffset = getNextDistributedDay();

              // Format description with keywords and hashtags for GMB
              const aiKeywords = post.keywords || [];
              const aiHashtags = post.hashtags || [];
              const formattedDescription = formatDescriptionWithTags(
                post.description || post.summary || '',
                aiKeywords,
                aiHashtags
              );

              allPosts.push({
                ...post,
                day_offset: dayOffset,
                description: formattedDescription,
                template_type: 'ai-generated',
                is_ai_generated: true,
                callToAction: post.callToAction || { actionType: 'CALL' }
              });
              actualAIPostsAdded++;
            }
            console.log(`[MonthlyPlan] PHASE 2 complete: Added ${actualAIPostsAdded} AI posts`);

            // If AI returned fewer posts than expected (quota issues, etc.), redistribute unused slots
            const unusedAISlots = effectiveAIPostTarget - actualAIPostsAdded;
            if (unusedAISlots > 0) {
              console.log(`[MonthlyPlan] AI returned fewer posts than expected - redistributing ${unusedAISlots} slots to reviews and GMB photos`);
              const extraReviews = Math.ceil(unusedAISlots * 0.5);
              const extraPhotos = unusedAISlots - extraReviews;
              targetReviewPosts += extraReviews;
              targetGMBPhotoPosts += extraPhotos;
              console.log(`[MonthlyPlan] Updated targets: Reviews=${targetReviewPosts}, GMB Photos=${targetGMBPhotoPosts}`);
            }
          } catch (err) {
            console.error('[MonthlyPlan] PHASE 2 ERROR - Failed to generate AI posts:', err?.message || err);
            // AI failed (quota exceeded, API error, etc.) - redistribute AI slots to reviews/photos
            const unusedAISlots = effectiveAIPostTarget - actualAIPostsAdded;
            if (unusedAISlots > 0) {
              console.log(`[MonthlyPlan] AI generation failed - redistributing ${unusedAISlots} slots to reviews and GMB photos`);
              // Add half to reviews, half to photos
              const extraReviews = Math.ceil(unusedAISlots * 0.5);
              const extraPhotos = unusedAISlots - extraReviews;
              targetReviewPosts += extraReviews;
              targetGMBPhotoPosts += extraPhotos;
              console.log(`[MonthlyPlan] Updated targets: Reviews=${targetReviewPosts}, GMB Photos=${targetGMBPhotoPosts}`);
            }
          }
        } else {
          console.log(`[MonthlyPlan] PHASE 2 skipped: effectiveAIPostTarget = ${effectiveAIPostTarget}`);
        }

        // PHASE 3: REVIEW POSTS
        updateGenerationProgress(4, 'Generating Review Posts...', 'active', allPosts.length);
        console.log(`[MonthlyPlan] PHASE 3 starting: targetReviewPosts=${targetReviewPosts}`);
        if (targetReviewPosts > 0) {
          try {
            // Get account and location names for GMB API
            // Try multiple sources for account name
            const accountName = defaultProfile?.account?.name
              || (defaultProfile?.accountId ? `accounts/${defaultProfile.accountId}` : null)
              || (defaultProfile?.account_id ? `accounts/${defaultProfile.account_id}` : null);
            const locationName = defaultProfile?.locationData?.name
              || (defaultProfile?.locationId ? `locations/${defaultProfile.locationId}` : null)
              || (defaultProfile?.location_id ? `locations/${defaultProfile.location_id}` : null)
              || (defaultProfile?.id ? `locations/${defaultProfile.id}` : null);

            console.log(`[MonthlyPlan] PHASE 3: Fetching reviews for account=${accountName}, location=${locationName}`);

            if (!accountName || !locationName) {
              console.warn('[MonthlyPlan] PHASE 3: Missing accountName or locationName, skipping reviews');
            } else {
              // Get access token from localStorage for GMBService
              let googleAccessToken = null;
              try {
                googleAccessToken = localStorage.getItem('googleAccessToken');
                if (!googleAccessToken) {
                  console.warn('[MonthlyPlan] PHASE 3: No Google access token in localStorage, GMBService will try to resolve one');
                }
              } catch (e) {
                console.warn('[MonthlyPlan] PHASE 3: Failed to get Google access token from localStorage:', e.message);
              }

              const reviewsData = await GMBService.getReviews(googleAccessToken, accountName, locationName);
              const reviews = reviewsData?.reviews || [];
              console.log(`[MonthlyPlan] PHASE 3: Got ${reviews.length} total reviews`);

              // Include 4-star and 5-star reviews with comments for more variety
              const goodReviews = reviews
                .filter(r => (r.starRating === 'FIVE' || r.starRating === 5 || r.starRating === 'FOUR' || r.starRating === 4) && r.comment)
                .sort(() => 0.5 - Math.random());
              console.log(`[MonthlyPlan] PHASE 3: Found ${goodReviews.length} 4-5 star reviews with comments`);

              for (const review of goodReviews) {
                if (actualReviewPostsAdded >= targetReviewPosts) break;

                const authorName = review.reviewer?.displayName || 'Happy Customer';
                const reviewText = review.comment || '';
                const avatarUrl = review.reviewer?.profilePhotoUrl || 'https://i.pravatar.cc/150?img=5';
                const starRating = (review.starRating === 'FIVE' || review.starRating === 5) ? 5 : 4;

                setTestimonialPreviewData({
                  title: "Customer",
                  subtitle: "Testimonial",
                  userProfile: { name: authorName, title: 'Verified Customer', avatar: avatarUrl },
                  testimonialText: reviewText,
                  rating: starRating,
                  ctaText: 'Experience Excellence'
                });

                await new Promise(r => setTimeout(r, 800));

                let imageUrl = null;

                if (testimonialPreviewRef.current) {
                  try {
                    const html2canvas = (await import('html2canvas')).default;
                    const canvas = await html2canvas(testimonialPreviewRef.current, {
                      backgroundColor: '#f8fafc',
                      scale: 2,
                      useCORS: true,
                      logging: false,
                      allowTaint: true
                    });

                    const base64Image = canvas.toDataURL('image/png', 1.0);

                    const uploadResp = await ApiService.post('/customer/auto-post/upload-image', {
                      imageData: base64Image,
                      filename: `testimonial_${Date.now()}`
                    });

                    if (uploadResp?.data?.imageUrl) {
                      const serverUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.visibeen.com';
                      imageUrl = `${serverUrl}${uploadResp.data.imageUrl}`;
                    }
                  } catch (err) {
                    console.warn('Failed to generate review image:', err);
                  }
                }

                setTestimonialPreviewData(null);

                // Always add the review post, even if image generation failed
                const dayOffset = getNextDistributedDay();

                // Generate AI description for review post
                let reviewDescription = `"${review.comment.substring(0, 150)}${review.comment.length > 150 ? '...' : ''}" - ${review.reviewer?.displayName || 'Customer'}`;
                let reviewKeywords = ['review', 'testimonial', 'customer feedback'];
                let reviewHashtags = ['#CustomerLove', `#${starRating}Stars`, '#HappyCustomer'];
                let reviewCaption = `Our customers love us! Read what ${review.reviewer?.displayName || 'our customer'} says.`;

                try {
                  const aiContent = await generateAIPostDescription('review', {
                    businessName: businessInfo.businessName,
                    category: businessInfo.businessCategory || defaultProfile?.locationData?.primaryCategory?.displayName || '',
                    city: businessInfo.businessCity || defaultProfile?.locationData?.storefrontAddress?.locality || '',
                    reviewerName: review.reviewer?.displayName || 'A valued customer',
                    rating: starRating,
                    reviewText: review.comment
                  });

                  if (aiContent) {
                    reviewDescription = aiContent.description || reviewDescription;
                    reviewCaption = aiContent.caption || reviewCaption;
                    if (aiContent.keywords?.length > 0) reviewKeywords = aiContent.keywords;
                    if (aiContent.hashtags?.length > 0) reviewHashtags = aiContent.hashtags;
                  }
                } catch (aiErr) {
                  logger.warn('[MonthlyPlan] AI review description failed, using default:', aiErr?.message);
                }

                allPosts.push({
                  day_offset: dayOffset,
                  time_of_day: '12:00',
                  title: `${'⭐'.repeat(starRating)} Customer Review`,
                  description: formatDescriptionWithTags(reviewDescription, reviewKeywords, reviewHashtags),
                  caption: reviewCaption,
                  topicType: 'TESTIMONIAL',
                  template_type: 'testimonial',
                  reviewer_name: review.reviewer?.displayName,
                  review_author: review.reviewer?.displayName,
                  review_text: review.comment,
                  review_rating: starRating,
                  generated_image_url: imageUrl || null,
                  is_testimonial: true,
                  callToAction: { actionType: 'CALL' },
                  keywords: reviewKeywords,
                  hashtags: reviewHashtags
                });
                actualReviewPostsAdded++;
                console.log(`[MonthlyPlan] Added review post #${actualReviewPostsAdded} from ${review.reviewer?.displayName}, hasImage: ${!!imageUrl}`);
              }
              console.log(`[MonthlyPlan] PHASE 3 complete: Added ${actualReviewPostsAdded} review posts (target: ${targetReviewPosts})`);
            }
          } catch (err) {
            console.error('[MonthlyPlan] PHASE 3 ERROR - Failed to generate review posts:', err?.message || err);
            console.error('[MonthlyPlan] PHASE 3 ERROR details:', err);
          }
        } else {
          console.log(`[MonthlyPlan] PHASE 3 skipped: targetReviewPosts = ${targetReviewPosts}`);
        }

        // PHASE 4: GMB PHOTOS
        updateGenerationProgress(5, 'Generating Photo Posts...', 'active', allPosts.length);
        // Calculate how many photo slots we need - fill all remaining after AI and reviews
        const targetPhotoSlots = limitedMaxPosts - allPosts.length;
        console.log(`[MonthlyPlan] PHASE 4 starting: limitedMaxPosts=${limitedMaxPosts}, allPosts.length=${allPosts.length}, targetPhotoSlots=${targetPhotoSlots}`);
        if (targetPhotoSlots > 0) {
          try {
            const photos = await fetchGMBPhotos();
            console.log(`[MonthlyPlan] PHASE 4: Fetched ${photos.length} GMB photos, need ${targetPhotoSlots}`);

            for (const photo of photos) {
              if (actualPhotoPostsAdded >= targetPhotoSlots) break;

              const dayOffset = getNextDistributedDay();

              // Generate AI description for GMB photo post
              let photoDescription = `Check out this photo from ${businessInfo.businessName}!`;
              let photoKeywords = ['photo', 'gallery', businessInfo.businessName?.toLowerCase()];
              let photoHashtags = ['#BusinessPhoto', '#VisitUs', '#PhotoGallery'];
              let photoCaption = `A glimpse of what awaits you at ${businessInfo.businessName}!`;

              try {
                const aiContent = await generateAIPostDescription('gmb-photo', {
                  businessName: businessInfo.businessName,
                  category: businessInfo.businessCategory || defaultProfile?.locationData?.primaryCategory?.displayName || '',
                  city: businessInfo.businessCity || defaultProfile?.locationData?.storefrontAddress?.locality || '',
                  photoType: photo.category || 'business',
                  photoDescription: photo.description || 'A photo from our business'
                });

                if (aiContent) {
                  photoDescription = aiContent.description || photoDescription;
                  photoCaption = aiContent.caption || photoCaption;
                  if (aiContent.keywords?.length > 0) photoKeywords = aiContent.keywords;
                  if (aiContent.hashtags?.length > 0) photoHashtags = aiContent.hashtags;
                }
              } catch (aiErr) {
                logger.warn('[MonthlyPlan] AI photo description failed, using default:', aiErr?.message);
              }

              allPosts.push({
                day_offset: dayOffset,
                time_of_day: '14:00',
                title: `📸 From our gallery`,
                description: formatDescriptionWithTags(photoDescription, photoKeywords, photoHashtags),
                caption: photoCaption,
                topicType: 'PHOTO',
                template_type: 'gmb-photo',
                generated_image_url: photo.url,
                is_gmb_photo: true,
                callToAction: { actionType: 'CALL' },
                keywords: photoKeywords,
                hashtags: photoHashtags
              });
              actualPhotoPostsAdded++;
            }
            console.log(`[MonthlyPlan] PHASE 4 complete: Added ${actualPhotoPostsAdded} GMB photo posts`);
          } catch (err) {
            console.error('[MonthlyPlan] PHASE 4 ERROR - Failed to generate photo posts:', err?.message || err);
            console.error('[MonthlyPlan] PHASE 4 ERROR details:', err);
          }
        } else {
          console.log(`[MonthlyPlan] PHASE 4 skipped: targetPhotoSlots = ${targetPhotoSlots}`);
        }

        // PHASE 5: LOG SUMMARY (No more fallback AI posts - AI limit is strict)
        // If reviews/photos aren't available, we don't fill with more AI posts
        const remainingAfterAllPhases = limitedMaxPosts - allPosts.length;
        if (remainingAfterAllPhases > 0) {
          console.log(`[MonthlyPlan] Note: ${remainingAfterAllPhases} slots unfilled. AI limit is strict - not generating additional AI posts.`);
          console.log(`[MonthlyPlan] To fill more slots, add more reviews or GMB photos to your profile.`);
        }

        console.log(`[MonthlyPlan] Final distribution: AI=${actualAIPostsAdded}, Reviews=${actualReviewPostsAdded}, Photos=${actualPhotoPostsAdded}, Total=${allPosts.length}/${limitedMaxPosts}`);
      }

      allPosts.sort((a, b) => a.day_offset - b.day_offset);

      // Mark the first post as IMMEDIATE - it will be posted to GMB right away
      if (allPosts.length > 0) {
        allPosts[0].is_immediate = true;
        allPosts[0].day_offset = 0; // Ensure it's for today
        console.log(`[MonthlyPlan] First post "${allPosts[0].title}" marked as IMMEDIATE`);
      }

      const monthlyPlan = {
        monthly_summary: isTrialGeneration
          ? `Trial: Generated ${allPosts.length} posts for the next ${GENERATION_PERIOD_DAYS} days`
          : `Generated ${allPosts.length} posts for the next 30 days`,
        business_focus: businessInfo.businessName,
        posts: allPosts,
        plan_code: planCode,
        max_posts: maxPosts,
        is_trial: isTrialGeneration,
        generated_date: new Date().toISOString()
      };

      setPlan(monthlyPlan);
      savePlanToCache(monthlyPlan);

      if (allPosts.length > 0) {
        logger.log('[MonthlyPlan] Calling schedule-plan with posts:', allPosts.length);
        logger.log('[MonthlyPlan] AI posts count:', allPosts.filter(p => p.template_type === 'ai-generated').length);
        logger.log('[MonthlyPlan] Festival posts count:', allPosts.filter(p => p.template_type === 'festival-edm').length);
        logger.log('[MonthlyPlan] Review posts count:', allPosts.filter(p => p.template_type === 'testimonial').length);
        logger.log('[MonthlyPlan] GMB photo posts count:', allPosts.filter(p => p.template_type === 'gmb-photo').length);

        // Check if admin is viewing another user's profile
        let targetUserId = null;
        try {
          // First try sessionStorage
          let adminAccessDataRaw = sessionStorage.getItem('adminAccessData');
          
          // If not in sessionStorage, try localStorage with tempKey (for new tabs opened by admin)
          if (!adminAccessDataRaw) {
            const tempKey = localStorage.getItem('latestAdminAccessTempKey');
            if (tempKey) {
              adminAccessDataRaw = localStorage.getItem(tempKey);
              // If found in localStorage, also set in sessionStorage for future use
              if (adminAccessDataRaw) {
                sessionStorage.setItem('adminAccessData', adminAccessDataRaw);
                logger.log(`[MonthlyPlan] Hydrated adminAccessData from localStorage tempKey: ${tempKey}`);
              }
            }
          }
          
          if (adminAccessDataRaw) {
            const adminAccess = JSON.parse(adminAccessDataRaw);
            if (adminAccess?.targetUserId) {
              targetUserId = adminAccess.targetUserId;
              logger.log(`[MonthlyPlan] Admin scheduling posts for user ${targetUserId}`);
            } else {
              logger.warn(`[MonthlyPlan] adminAccessData found but no targetUserId:`, adminAccess);
            }
          } else {
            logger.log(`[MonthlyPlan] No adminAccessData found in storage - user is scheduling for themselves`);
          }
        } catch (e) {
          logger.error(`[MonthlyPlan] Error reading adminAccessData:`, e);
        }
        
        logger.log(`[MonthlyPlan] Final targetUserId for schedule request: ${targetUserId || 'null (own user)'}`);
        

        const scheduleResponse = await ApiService.post('/customer/auto-post/schedule-plan', {
          gmbAccountId,
          accountId: cleanAccountId,
          locationId: cleanLocationId,
          posts: allPosts,
          locationData: defaultProfile?.locationData || null,
          businessName: businessInfo.businessName,
          targetUserId // Include when admin is scheduling for another user
        });

        logger.log('[MonthlyPlan] Schedule response:', scheduleResponse?.data || scheduleResponse);

        // Check for errors in the response
        const responseData = scheduleResponse?.data || scheduleResponse || {};
        const scheduledCount = responseData.scheduledCount || responseData.summary?.succeeded || 0;
        const failedCount = responseData.errors?.length || responseData.summary?.failed || 0;
        const errors = responseData.errors || [];

        if (failedCount > 0 || errors.length > 0) {
          logger.error('[MonthlyPlan] Some posts failed to save:', errors);
          const errorMessage = `Warning: ${failedCount} post(s) failed to save. Check console for details.`;
          setError(errorMessage);
          // Continue to reload posts from DB to show what did save successfully
        }

        logger.log(`[MonthlyPlan] Successfully scheduled ${scheduledCount} posts, ${failedCount} failed`);

        const reloadedPosts = await loadScheduledPostsFromDB(cleanLocationId);
        logger.log('[MonthlyPlan] Reloaded posts from DB:', reloadedPosts.length);
        if (reloadedPosts.length > 0) {
          const updatedPlan = {
            ...monthlyPlan,
            posts: reloadedPosts
          };
          setPlan(updatedPlan);
          savePlanToCache(updatedPlan);

          // Mark generation as complete with success
          setGenerationComplete(true);
          setGenerationResult({
            success: true,
            totalPosts: reloadedPosts.length,
            aiPosts: reloadedPosts.filter(p => p.template_type === 'ai-generated').length,
            reviewPosts: reloadedPosts.filter(p => p.template_type === 'testimonial').length,
            photoPosts: reloadedPosts.filter(p => p.template_type === 'gmb-photo').length,
            festivalPosts: reloadedPosts.filter(p => p.template_type === 'festival-edm').length
          });
          updateGenerationProgress(5, 'Complete!', 'completed', reloadedPosts.length);
        } else {
          logger.warn('[MonthlyPlan] No posts reloaded from DB - using generated posts as fallback');
          // Use the generated posts as fallback if DB reload fails
          setPlan(monthlyPlan);
          savePlanToCache(monthlyPlan);

          // Mark generation as complete
          setGenerationComplete(true);
          setGenerationResult({
            success: true,
            totalPosts: allPosts.length,
            aiPosts: allPosts.filter(p => p.template_type === 'ai-generated').length,
            reviewPosts: allPosts.filter(p => p.template_type === 'testimonial').length,
            photoPosts: allPosts.filter(p => p.template_type === 'gmb-photo').length,
            festivalPosts: allPosts.filter(p => p.template_type === 'festival-edm').length
          });
          updateGenerationProgress(5, 'Complete!', 'completed', allPosts.length);
        }
      }
    } catch (err) {
      logger.error('[MonthlyPlan] Monthly generation failed', err);
      setError(err?.message || 'Failed to generate monthly posts');
      setGenerationComplete(true);
      setGenerationResult({
        success: false,
        error: err?.message || 'Failed to generate monthly posts'
      });
    } finally {
      setCombinedLoading(false);
      isGeneratingRef.current = false;
      setGenerationProgress(prev => ({ ...prev, isGenerating: false }));
    }
  }, [defaultProfile, planCode, stablePlanCode, currentMonth, manualProducts, manualServices, loadScheduledPostsFromDB, fetchGMBPhotos, savePlanToCache, gmbAccountId, isTrialUser, updateGenerationProgress, initialPostsLoaded, generationStatus]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <PageContainer>
      {/* GENERATION PROGRESS MODAL - Professional Loading Popup */}
      <Dialog
        open={showGenerationModal}
        onClose={() => { }}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxWidth: '480px',
            width: '100%',
            m: 2,
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 3,
          textAlign: 'center'
        }}>
          <Typography sx={{ color: 'white', fontSize: '22px', fontWeight: 700, mb: 0.5 }}>
            {generationComplete ? (generationResult?.success ? 'Generation Complete!' : 'Generation Failed') : 'Generating Your Monthly Plan'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
            {generationComplete
              ? (generationResult?.success ? 'Your posts are ready to publish' : 'Please try again')
              : 'Please wait while we create your content'}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {!generationComplete ? (
            <>
              {/* Progress Steps */}
              <Box sx={{ mb: 3 }}>
                {generationProgress.phases.map((phase, index) => (
                  <Box
                    key={phase.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 1.5,
                      borderBottom: index < generationProgress.phases.length - 1 ? '1px solid #f1f5f9' : 'none'
                    }}
                  >
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      background: phase.status === 'completed' ? '#10b981'
                        : phase.status === 'active' ? '#667eea'
                          : '#f1f5f9',
                      color: phase.status === 'pending' ? '#94a3b8' : 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      {phase.status === 'completed' ? '✓' : phase.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{
                        fontSize: '14px',
                        fontWeight: phase.status === 'active' ? 600 : 500,
                        color: phase.status === 'pending' ? '#94a3b8'
                          : phase.status === 'active' ? '#1e293b'
                            : '#10b981'
                      }}>
                        {phase.name}
                      </Typography>
                      {phase.status === 'active' && (
                        <LinearProgress
                          sx={{
                            mt: 0.5,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: 2
                            }
                          }}
                        />
                      )}
                    </Box>
                    {phase.status === 'completed' && (
                      <Typography sx={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                        Done
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Stats */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 2,
                background: '#f8fafc',
                borderRadius: '12px',
                mb: 2
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#667eea' }}>
                    {generationProgress.postsGenerated}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Posts Created
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#667eea' }}>
                    {generationProgress.phaseNumber}/{generationProgress.totalPhases}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Phases
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#667eea' }}>
                    ~{generationProgress.estimatedTimeRemaining}s
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Remaining
                  </Typography>
                </Box>
              </Box>

              {/* Info Banner */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 2,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: '12px',
                border: '1px solid #fbbf24'
              }}>
                <Typography sx={{ fontSize: '20px' }}>💡</Typography>
                <Typography sx={{ fontSize: '13px', color: '#92400e', lineHeight: 1.4 }}>
                  You can navigate away - generation will continue in the background!
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {/* Completion Result */}
              {generationResult?.success ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '36px',
                    color: 'white'
                  }}>
                    ✓
                  </Box>

                  <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    {generationResult.totalPosts} Posts Created!
                  </Typography>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 1.5,
                    mb: 3
                  }}>
                    {generationResult.aiPosts > 0 && (
                      <Box sx={{ p: 1.5, background: '#ede9fe', borderRadius: '10px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#7c3aed' }}>
                          {generationResult.aiPosts}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#6b21a8' }}>AI Posts</Typography>
                      </Box>
                    )}
                    {generationResult.reviewPosts > 0 && (
                      <Box sx={{ p: 1.5, background: '#fef3c7', borderRadius: '10px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#d97706' }}>
                          {generationResult.reviewPosts}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#92400e' }}>Review Posts</Typography>
                      </Box>
                    )}
                    {generationResult.photoPosts > 0 && (
                      <Box sx={{ p: 1.5, background: '#dbeafe', borderRadius: '10px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#2563eb' }}>
                          {generationResult.photoPosts}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#1d4ed8' }}>Photo Posts</Typography>
                      </Box>
                    )}
                    {generationResult.festivalPosts > 0 && (
                      <Box sx={{ p: 1.5, background: '#fce7f3', borderRadius: '10px', textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#db2777' }}>
                          {generationResult.festivalPosts}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#9d174d' }}>Festival Posts</Typography>
                      </Box>
                    )}
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setShowGenerationModal(false)}
                    sx={{
                      py: 1.5,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: 600,
                      fontSize: '15px',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
                      }
                    }}
                  >
                    View Your Posts
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '36px',
                    color: 'white'
                  }}>
                    ✕
                  </Box>

                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#1e293b', mb: 1 }}>
                    Something went wrong
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#64748b', mb: 3 }}>
                    {generationResult?.error || 'Please try again later'}
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setShowGenerationModal(false)}
                    sx={{
                      py: 1.5,
                      borderRadius: '12px',
                      background: '#1e293b',
                      fontWeight: 600,
                      fontSize: '15px',
                      textTransform: 'none',
                      '&:hover': {
                        background: '#0f172a'
                      }
                    }}
                  >
                    Close
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Dialog>

      {/* ACCESS CONTROL: Checking state */}
      {accessChecking && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <CircularProgress size={48} sx={{ color: '#667eea' }} />
          <Typography sx={{ color: '#64748b', fontSize: '15px' }}>
            Verifying your subscription...
          </Typography>
        </Box>
      )}

      {/* ACCESS CONTROL: Locked state - show Coming Soon */}
      {!accessChecking && !hasPaymentAccess && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          p: 4,
          background: 'linear-gradient(180deg, rgba(248, 250, 252, 0) 0%, rgba(241, 245, 249, 0.5) 100%)',
          borderRadius: '24px',
          m: 3
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: '0 10px 25px -5px rgba(100, 116, 139, 0.25)'
          }}>
            <TimeIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 2, letterSpacing: '-0.5px' }}>
            Coming Soon 🚀
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '16px', mb: 4, maxWidth: 450, lineHeight: 1.6 }}>
            The Monthly Plan feature for automating your Google Business posts is currently being rolled out to select users. Stay tuned!
          </Typography>
          <Chip
            label="In Development"
            sx={{
              bgcolor: 'rgba(100, 116, 139, 0.1)',
              color: '#64748b',
              fontWeight: 600,
              fontSize: '13px',
              px: 1
            }}
          />
        </Box>
      )}

      {/* NORMAL CONTENT: Only render when access is granted */}
      {!accessChecking && hasPaymentAccess && (
        <>
          {/* MULTI-TAB: Visual indicator for secondary tabs */}
          {!isActiveTab && (
            <Box
              sx={{
                position: 'fixed',
                top: 10,
                right: 10,
                zIndex: 9999,
                background: 'rgba(251, 191, 36, 0.9)',
                color: '#92400e',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#f59e0b',
                  animation: 'pulse 2s infinite'
                }}
              />
              Viewing Only (Active tab in another window)
              <style>
                {`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}
              </style>
            </Box>
          )}

          {/* GMB Profile Selection Dialog */}
          <Dialog
            open={profileSelectionDialogOpen}
            onClose={() => setProfileSelectionDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }
            }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
              <BusinessIcon sx={{ color: '#667eea', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Select Business Profile
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography sx={{ color: '#64748b', mb: 3 }}>
                Choose a GMB profile to generate your monthly content plan.
              </Typography>
              {profilesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={40} sx={{ color: '#667eea' }} />
                </Box>
              ) : profiles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BusinessIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                  <Typography sx={{ color: '#64748b', mb: 2 }}>
                    No GMB profiles found. Connect your Google Business account.
                  </Typography>
                  <ActionButton
                    variant="contained"
                    onClick={() => { setProfileSelectionDialogOpen(false); navigate('/connect-google'); }}
                    sx={{ background: '#667eea', '&:hover': { background: '#5567d9' } }}
                  >
                    <LinkIcon sx={{ mr: 1, fontSize: 18 }} />
                    Connect Google
                  </ActionButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {profiles.map((profile, index) => (
                    <Box
                      key={profile.locationId}
                      onClick={() => handleSelectProfileFromDialog(profile)}
                      data-tutorial={index === 0 ? "monthly-plan-profile" : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#667eea',
                          background: 'rgba(102, 126, 234, 0.05)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                        }
                      }}
                    >
                      <ProfileAvatar sx={{ width: 48, height: 48, fontSize: 18 }}>
                        {getInitials(profile.name)}
                      </ProfileAvatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '15px' }}>
                          {profile.name || 'Business Profile'}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '13px' }}>
                          {profile.address || 'Google Business Profile'}
                        </Typography>
                      </Box>
                      <BusinessIcon sx={{ color: '#cbd5e1' }} />
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ pt: 2 }}>
              <Button
                onClick={() => {
                  setProfileSelectionDialogOpen(false);
                  setProfileDialogDismissed(true);
                }}
                sx={{ color: '#64748b' }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={preparingPlan}
            PaperProps={{
              sx: {
                borderRadius: '24px',
                padding: '40px',
                textAlign: 'center',
                alignItems: 'center',
                maxWidth: '400px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
              <CircularProgress size={80} thickness={3} sx={{ color: '#8b5cf6' }} />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AIIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
              </Box>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#1e293b' }}>
              Preparing Monthly Plan
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 1, lineHeight: 1.6 }}>
              Our AI is analyzing your profile to generate and schedule your content for the entire month.
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
              This usually takes about 10-15 seconds...
            </Typography>
          </Dialog>

          {/* Header Section - Mobile Optimized */}
          <HeaderCard>
            {/* Mobile Header - Compact */}
            <Box sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ color: '#667eea', fontSize: 24 }} />
                <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '18px' }}>
                  Monthly Plan
                </Typography>
              </Box>
              {defaultProfile && (
                <IconButton
                  size="small"
                  onClick={() => {
                    loadProfiles();
                    setProfileSelectionDialogOpen(true);
                  }}
                  sx={{
                    background: '#f1f5f9',
                    '&:hover': { background: '#e2e8f0' }
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {/* Desktop Header - Full */}
            <Box sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 3
            }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <CalendarIcon sx={{ color: '#667eea', fontSize: 32 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '24px', md: '28px' } }}>
                    Monthly Plan
                  </Typography>
                </Box>
                <Typography sx={{ color: '#64748b', fontSize: '15px', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AIIcon sx={{ fontSize: 18, color: '#667eea' }} />
                  AI-generated posts scheduled for your Google Business Profile
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {!defaultProfile && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel sx={{ fontSize: '13px' }}>Select Business</InputLabel>
                      <ControlSelect
                        value={selectedProfileId}
                        label="Select Business"
                        disabled={profilesLoading || profiles.length === 0}
                        onChange={(e) => setSelectedProfileId(e.target.value)}
                      >
                        {profiles.map((p) => (
                          <MenuItem key={p.locationId} value={p.locationId}>
                            {p.name || p.locationId}
                          </MenuItem>
                        ))}
                      </ControlSelect>
                    </FormControl>
                    <ActionButton variant="contained" onClick={handleSelectProfile} disabled={!selectedProfileId}>
                      Set Profile
                    </ActionButton>
                    <ActionButton variant="outlined" onClick={handleConnect} sx={{ borderColor: '#667eea', color: '#667eea' }}>
                      <LinkIcon sx={{ mr: 1, fontSize: 18 }} />
                      Connect GMB
                    </ActionButton>
                  </>
                )}
              </Box>
            </Box>

            {/* Profile Card - Mobile Compact */}
            {defaultProfile?.name && (
              <ProfileCard>
                <ProfileAvatar>
                  {getInitials(defaultProfile.name)}
                </ProfileAvatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: { xs: '15px', md: '18px' },
                    fontWeight: 600,
                    mb: 0.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {defaultProfile.name}
                  </Typography>
                  <Typography sx={{
                    opacity: 0.85,
                    fontSize: { xs: '12px', md: '14px' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {defaultProfile.address || 'Business Profile Connected'}
                  </Typography>
                </Box>
                {/* Change button - Desktop only */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                  <ActionButton
                    variant="outlined"
                    onClick={() => {
                      loadProfiles();
                      setProfileSelectionDialogOpen(true);
                    }}
                    sx={{ borderColor: '#667eea', color: '#667eea' }}
                  >
                    <RefreshIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    Change
                  </ActionButton>
                  <Chip
                    icon={<CheckIcon sx={{ color: '#10b981 !important', fontSize: 16 }} />}
                    label="Active"
                    sx={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      fontWeight: 500,
                      '& .MuiChip-icon': { color: '#10b981' }
                    }}
                  />
                </Box>
                {/* Status chip - Mobile */}
                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                  <Chip
                    size="small"
                    icon={<CheckIcon sx={{ color: '#10b981 !important', fontSize: 14 }} />}
                    label="Active"
                    sx={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      fontWeight: 500,
                      fontSize: '11px',
                      height: '24px',
                      '& .MuiChip-icon': { color: '#10b981' }
                    }}
                  />
                </Box>
              </ProfileCard>
            )}
          </HeaderCard>

          {/* Error Banner */}
          {error && (
            <Box className="error-banner">
              <Box className="error-icon">
                <ErrorIcon />
              </Box>
              <Typography className="error-text">{error}</Typography>
            </Box>
          )}

          {/* Action Buttons - Show when profile is selected */}
          {defaultProfile && (
            <Box sx={{
              mb: 4,
              p: 3,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              borderRadius: '16px',
              border: '1px solid #e2e8f0'
            }}>
              {/* Content Generation section removed */}
            </Box>
          )}


          {/* Calendar Grid - New Styled Design */}
          {plan && (
            <Box sx={{ mb: 4 }}>
              <FullCalendarView
                posts={plan.posts || []}
                onEventClick={setSelectedPost}
                currentMonth={currentMonth}
              />
            </Box>
          )}

          {/* Loading State */}
          {loading && (
            <Box className="loading-container">
              <Box className="loading-spinner" />
              <Typography className="loading-text">Generating your monthly plan...</Typography>
              <Typography className="loading-subtext">This may take a minute while we create posts</Typography>
            </Box>
          )}

          {/* Empty State - Mobile Optimized */}
          {!loading && !error && !plan && (
            <Box className="empty-state" sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 4, md: 6 },
              minHeight: { xs: '40vh', md: '50vh' }
            }}>
              <Box className="empty-icon" sx={{
                width: { xs: 56, md: 80 },
                height: { xs: 56, md: 80 },
                background: !initialPostsLoaded
                  ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}>
                {!initialPostsLoaded ? (
                  <CircularProgress size={isMobile ? 24 : 32} sx={{ color: 'white' }} />
                ) : (
                  <CalendarIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                )}
              </Box>
              <Typography className="empty-title" sx={{
                fontSize: { xs: '18px', md: '24px' },
                px: 2
              }}>
                {!initialPostsLoaded ? 'Checking Your Posts...' : 'No Monthly Plan Yet'}
              </Typography>
              <Typography className="empty-description" sx={{
                fontSize: { xs: '14px', md: '16px' },
                maxWidth: { xs: 260, md: 500 }
              }}>
                {!initialPostsLoaded
                  ? 'Please wait while we check for existing scheduled posts'
                  : 'Select a business profile and tap the button below to create your posting calendar'}
              </Typography>
              {/* Desktop button - centered */}
              <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 3 }}>
                <ActionButton
                  variant="contained"
                  onClick={handleAutoGenerateMonth}
                  disabled={!gmbAccountId || combinedLoading || !initialPostsLoaded || !generationStatus.canGenerate}
                  data-tutorial="auto-generate-month"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #5567d9 0%, #653a91 100%)' }
                  }}
                  startIcon={!initialPostsLoaded ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
                >
                  {combinedLoading ? 'Generating...' : !initialPostsLoaded ? 'Checking...' : !generationStatus.canGenerate ? `Wait ${generationStatus.daysRemaining} days` : 'Auto Generate Month'}
                </ActionButton>
              </Box>
            </Box>
          )}

          {/* Mobile Fixed Bottom Action Bar */}
          {defaultProfile && !loading && (
            <Box sx={{
              display: { xs: 'block', md: 'none' },
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 100,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              padding: '12px 16px',
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              borderTop: '1px solid #e5e7eb',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)'
            }}>
              {/* Show generate button if can generate */}
              {(!plan || (plan && generationStatus.canGenerate)) && initialPostsLoaded && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAutoGenerateMonth}
                  disabled={!gmbAccountId || combinedLoading || !generationStatus.canGenerate}
                  startIcon={combinedLoading ? <CircularProgress size={18} color="inherit" /> : <AIIcon />}
                  sx={{
                    height: 48,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 600,
                    fontSize: '15px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5567d9 0%, #653a91 100%)',
                      boxShadow: 'none'
                    },
                    '&.Mui-disabled': {
                      background: '#e2e8f0',
                      color: '#94a3b8'
                    }
                  }}
                >
                  {combinedLoading ? 'Generating...' : (plan ? 'Generate New Plan' : 'Auto Generate Month')}
                </Button>
              )}

              {/* Show loading state while checking for existing posts */}
              {!initialPostsLoaded && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  py: 0.5
                }}>
                  <CircularProgress size={20} sx={{ color: '#667eea' }} />
                  <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                    Checking scheduled posts...
                  </Typography>
                </Box>
              )}

              {/* Show status when posts exist and can't generate */}
              {plan && !generationStatus.canGenerate && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  py: 0.5
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckIcon sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                      {generationStatus.upcomingPostsCount} Posts Scheduled
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                      {generationStatus.daysRemaining} days until new plan available
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Plan Status Banner - Show when posts exist - Mobile Optimized */}
          {!loading && !error && plan && plan.posts && plan.posts.length > 0 && (
            <Box sx={{
              mb: { xs: 2, md: 4 },
              p: { xs: 2, md: 3 },
              background: generationStatus.canGenerate
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
              borderRadius: { xs: '12px', md: '16px' },
              border: '1px solid',
              borderColor: generationStatus.canGenerate ? '#e2e8f0' : '#d1fae5',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, md: 2 } }}>
                <Box sx={{
                  width: { xs: 40, md: 48 },
                  height: { xs: 40, md: 48 },
                  borderRadius: { xs: '10px', md: '12px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: generationStatus.canGenerate
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                }}>
                  {generationStatus.canGenerate ? (
                    <AIIcon sx={{ color: 'white', fontSize: { xs: 20, md: 24 } }} />
                  ) : (
                    <CheckIcon sx={{ color: 'white', fontSize: { xs: 20, md: 24 } }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    fontWeight: 600,
                    fontSize: { xs: '14px', md: '16px' },
                    color: '#1e293b',
                    mb: 0.25
                  }}>
                    {generationStatus.canGenerate
                      ? 'Ready for New Posts!'
                      : `${generationStatus.upcomingPostsCount} Posts Scheduled`
                    }
                  </Typography>
                  <Typography sx={{
                    fontSize: { xs: '12px', md: '14px' },
                    color: '#64748b',
                    lineHeight: 1.4
                  }}>
                    {generationStatus.canGenerate
                      ? 'No upcoming posts. Generate your content now!'
                      : `Posts auto-publish. New plan available after completion.`
                    }
                  </Typography>
                </Box>
              </Box>

              {/* Desktop: Show generate button only if cooldown has passed and posts are loaded */}
              {generationStatus.canGenerate && initialPostsLoaded && (
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <ActionButton
                    variant="contained"
                    onClick={handleAutoGenerateMonth}
                    disabled={!gmbAccountId || combinedLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #5567d9 0%, #653a91 100%)' }
                    }}
                    startIcon={<AIIcon />}
                  >
                    {combinedLoading ? 'Generating...' : 'Generate New Plan'}
                  </ActionButton>
                </Box>
              )}

              {/* Show countdown chip when on cooldown */}
              {!generationStatus.canGenerate && generationStatus.daysRemaining > 0 && (
                <Chip
                  size="small"
                  icon={<ScheduleIcon sx={{ color: '#94a3b8 !important', fontSize: { xs: 14, md: 18 } }} />}
                  label={`${generationStatus.daysRemaining} days remaining`}
                  sx={{
                    background: '#f1f5f9',
                    color: '#64748b',
                    fontWeight: 500,
                    fontSize: { xs: '11px', md: '13px' },
                    height: { xs: 28, md: 32 },
                    '& .MuiChip-icon': { color: '#94a3b8' },
                    alignSelf: { xs: 'flex-start', md: 'center' }
                  }}
                />
              )}
            </Box>
          )}

          {/* Products/Services Prompt */}
          <Dialog
            open={productServiceDialogOpen}
            onClose={handleCloseProductDialog}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
          >
            <DialogTitle sx={{ fontWeight: 700 }}>Tell us about your products & services</DialogTitle>
            <DialogContent dividers>
              <Typography sx={{ mb: 2, color: '#475569' }}>
                We will prioritize these items in your weekly posts and AI image prompts. Separate entries with commas or new lines.
              </Typography>
              <TextField
                label="Products"
                placeholder="e.g. Men's denim jackets, Leather belts"
                fullWidth
                multiline
                minRows={3}
                value={productInput}
                onChange={(e) => setProductInput(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Services"
                placeholder="e.g. Custom tailoring, Free fittings"
                fullWidth
                multiline
                minRows={3}
                value={serviceInput}
                onChange={(e) => setServiceInput(e.target.value)}
              />

              {(manualProducts.length > 0 || manualServices.length > 0) && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {manualProducts.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Saved Products:</Typography>
                      {manualProducts.map((item, idx) => (
                        <Chip key={`${item}-${idx}`} label={item} size="small" />
                      ))}
                    </Box>
                  )}
                  {manualServices.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Saved Services:</Typography>
                      {manualServices.map((item, idx) => (
                        <Chip key={`${item}-${idx}`} label={item} size="small" />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button onClick={() => handleSkipProductServices()} color="secondary">
                Skip for now
              </Button>
              <Button onClick={() => handleSaveProductServices(false)}>
                Save
              </Button>
              <Button variant="contained" onClick={() => handleSaveProductServices(true)}>
                Save & Generate
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Post Dialog */}
          <Dialog
            open={editPostDialogOpen}
            onClose={() => setEditPostDialogOpen(false)}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                zIndex: 15000,
                position: 'relative'
              }
            }}
            BackdropProps={{
              sx: {
                zIndex: 14999
              }
            }}
            sx={{
              zIndex: 15000,
              position: 'relative'
            }}
          >
            <DialogTitle sx={{ fontWeight: 700 }}>Edit Post</DialogTitle>
            <DialogContent dividers>
              <TextField
                label="Post Title"
                fullWidth
                multiline
                minRows={2}
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Post Description"
                fullWidth
                multiline
                minRows={4}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Image URL"
                fullWidth
                value={editFormData.post_url}
                onChange={(e) => setEditFormData({ ...editFormData, post_url: e.target.value })}
                sx={{ mb: 2 }}
                helperText="URL to the post image (optional)"
              />
              <TextField
                label="Scheduled Date & Time"
                type="datetime-local"
                fullWidth
                value={editFormData.scheduled_time}
                onChange={(e) => setEditFormData({ ...editFormData, scheduled_time: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Keywords"
                fullWidth
                value={editFormData.keywords}
                onChange={(e) => setEditFormData({ ...editFormData, keywords: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Comma-separated keywords (e.g., local business, quality service)"
              />
              <TextField
                label="Hashtags"
                fullWidth
                value={editFormData.hashtags}
                onChange={(e) => setEditFormData({ ...editFormData, hashtags: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Comma-separated hashtags (e.g., #LocalBusiness, #BestService)"
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button onClick={() => setEditPostDialogOpen(false)} color="secondary">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEditPost}
                disabled={loading || !editFormData.title.trim()}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Post Detail Modal - Mobile Optimized (Bottom Sheet) */}
          {selectedPost && (
            <Box className="modal-overlay" onClick={() => setSelectedPost(null)}>
              <Box className="modal-content" onClick={(e) => e.stopPropagation()}>
                <Box className="modal-header">
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '15px', md: '18px' },
                      lineHeight: 1.3,
                      flex: 1,
                      pr: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {selectedPost.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {selectedPost.db_id && (
                      <>
                        <IconButton
                          className="modal-action-btn"
                          onClick={() => handleEditPost(selectedPost)}
                          title="Edit post"
                          sx={{ padding: '8px', minWidth: 44, minHeight: 44 }}
                        >
                          <EditIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                        </IconButton>
                        <IconButton
                          className="modal-action-btn delete"
                          onClick={() => handleDeletePost(selectedPost)}
                          title="Delete post"
                          sx={{ padding: '8px', minWidth: 44, minHeight: 44, '&.delete': { color: '#ef4444' } }}
                        >
                          <DeleteIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                        </IconButton>
                      </>
                    )}
                    <IconButton className="modal-close" onClick={() => setSelectedPost(null)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Box className="modal-body">
                  <Box className="post-meta" sx={{ mb: { xs: 1.5, md: 2.5 } }}>
                    <Box className="meta-badge">
                      <TodayIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                      Day {selectedPost.day_offset ?? 0}
                    </Box>
                    <Box className="meta-badge">
                      <TimeIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                      {selectedPost.time_of_day || '10:00'}
                    </Box>
                    <Box className="meta-badge primary">
                      {selectedPost.topicType || 'STANDARD'}
                    </Box>
                  </Box>

                  {selectedPost.generated_image_url && (
                    <Box className="post-image-container" sx={{ position: 'relative', minHeight: { xs: 150, md: 200 } }}>
                      {/* Loading placeholder */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f1f5f9',
                          borderRadius: '8px',
                          zIndex: 0
                        }}
                      >
                        <CircularProgress size={24} sx={{ color: '#94a3b8' }} />
                      </Box>
                      <img
                        src={getImageUrl(selectedPost.generated_image_url)}
                        alt={selectedPost.title}
                        referrerPolicy="no-referrer"
                        loading="lazy"
                        onClick={() => setFullImageUrl(getImageUrl(selectedPost.generated_image_url))}
                        title="Tap to view full image"
                        style={{ position: 'relative', zIndex: 1 }}
                        onLoad={(e) => {
                          // Hide loading placeholder on successful load
                          const placeholder = e.target.previousElementSibling;
                          if (placeholder) placeholder.style.display = 'none';
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/600x400?text=Image+Not+Available';
                          e.target.alt = 'Image not available';
                          // Hide loading placeholder on error too
                          const placeholder = e.target.previousElementSibling;
                          if (placeholder) placeholder.style.display = 'none';
                        }}
                      />
                    </Box>
                  )}

                  {selectedPost.image_error && (
                    <Box className="error-banner" sx={{ mb: { xs: 1.5, md: 2 } }}>
                      <Box className="error-icon"><ErrorIcon sx={{ fontSize: { xs: 18, md: 20 } }} /></Box>
                      <Typography className="error-text">
                        Image generation failed: {selectedPost.image_error}
                      </Typography>
                    </Box>
                  )}

                  <Box className="section-block">
                    <Typography className="section-label">Description</Typography>
                    <Typography className="section-text">{selectedPost.description}</Typography>
                  </Box>

                  {selectedPost.caption && (
                    <Box className="section-block">
                      <Typography className="section-label">Caption</Typography>
                      <Typography className="section-text">{selectedPost.caption}</Typography>
                    </Box>
                  )}

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 1.5, md: 2 },
                    mb: { xs: 1.5, md: 2 }
                  }}>
                    <Box className="section-block">
                      <Typography className="section-label">Keywords</Typography>
                      <Box className="tags-container">
                        {(selectedPost.keywords || []).map((kw, i) => (
                          <span key={i} className="tag keyword">{kw}</span>
                        ))}
                      </Box>
                    </Box>
                    <Box className="section-block">
                      <Typography className="section-label">Hashtags</Typography>
                      <Box className="tags-container">
                        {(selectedPost.hashtags || []).map((tag, i) => (
                          <span key={i} className="tag hashtag">{tag}</span>
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  {selectedPost.callToAction?.actionType && (
                    <Box className="section-block">
                      <Typography className="section-label">Call to Action</Typography>
                      <Box className="cta-badge">
                        <LinkIcon sx={{ fontSize: 18 }} />
                        {selectedPost.callToAction.actionType}
                      </Box>
                    </Box>
                  )}

                  <Box className="section-block">
                    <Typography className="section-label">Image Prompt</Typography>
                    <Box className="image-prompt-box">
                      {selectedPost.image_prompt || 'No prompt available'}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Hidden Template Preview Container for Capture */}
          {testimonialPreviewData && (
            <Box
              ref={testimonialPreviewRef}
              sx={{
                position: 'fixed',
                left: '-9999px',
                top: 0,
                width: '716px',
                height: 'auto',
                minHeight: '500px',
                zIndex: -1,
                backgroundColor: '#f8fafc',
                overflow: 'visible'
              }}
            >
              <EnhancedPostPreview
                generatedPost={testimonialPreviewData}
                selectedTemplate={enhancedTemplateData.templatePreviews[0]}
              />
            </Box>
          )}

          {/* Hidden EDM Template Preview Container for Capture */}
          {edmPreviewData && (
            <Box
              ref={edmPreviewRef}
              sx={{
                position: 'fixed',
                left: '-9999px',
                top: 0,
                width: '500px',
                height: 'auto',
                zIndex: -1,
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                borderRadius: '12px'
              }}
            >
              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={edmPreviewData.festivalImage}
                    alt={edmPreviewData.festivalName}
                    sx={{
                      width: '500px',
                      height: '500px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    crossOrigin="anonymous"
                  />
                </Box>

                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  background: '#0F172A',
                  padding: '16px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTop: '3px solid #D4AF37',
                  gap: '12px'
                }}>
                  <Box sx={{
                    backgroundColor: '#ffffff',
                    padding: '6px 20px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '150px'
                  }}>
                    <Typography sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#0F172A',
                      fontFamily: "'Montserrat', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      lineHeight: 1.2
                    }}>
                      {edmPreviewData.businessName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '12px', width: '100%' }}>
                    {edmPreviewData.businessAddress && (
                      <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontFamily: 'Inter, sans-serif', maxWidth: '280px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {edmPreviewData.businessAddress}
                      </Typography>
                    )}
                    {edmPreviewData.businessPhone && (
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#F8FAFC', fontFamily: 'Inter, sans-serif', letterSpacing: '0.5px' }}>
                        {edmPreviewData.businessPhone}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Full Image Lightbox */}
          {fullImageUrl && (
            <Box className="lightbox-overlay" onClick={() => setFullImageUrl(null)}>
              <IconButton className="lightbox-close" onClick={() => setFullImageUrl(null)}>
                <CloseIcon />
              </IconButton>
              <img
                src={fullImageUrl}
                alt="Full size preview"
                className="lightbox-image"
                onClick={(e) => e.stopPropagation()}
              />
              <Typography className="lightbox-hint">
                Click anywhere or press ESC to close
              </Typography>
            </Box>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default MonthlyPlan;